// IndexedDB-backed offline queue for high-frequency operational writes.
// Wired into the most-painful flows to lose when the packhouse Wi-Fi blips,
// or when a phone in the orchard is on the wrong side of a hill:
//   - tipping_log inserts
//   - pallet creations (with photo bytes captured locally)
//   - scouting_observations (mig 275; with photo bytes captured locally)
//   - harvest_picks (mig 268; orchard pick logging from the field)
//   - spray_records (mig 228; spray applications logged at spray time)
//
// Status transitions on existing rows aren't queued — they need version
// checks + cell-uniqueness reads that don't make sense offline.
//
// Behaviour:
//   - When online: try the write directly. If it fails for *any* network-y
//     reason, push to the queue and surface a "queued" success.
//   - When offline: skip the direct attempt, push to the queue immediately.
//   - Background flusher: tries to drain the queue on `window.online` and
//     every minute while online.
//
// Storage: native IndexedDB (no `idb` dependency). One store with a `kind`
// discriminator so the drainer can dispatch by kind. Pallet entries also
// carry a Blob (photo bytes) — IndexedDB stores Blobs natively.

import { withCapturedAt } from './offlineQueue/capturedAt'
import { type SupabaseClient } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { sastTodayYMD } from '@/lib/dates'
import { spanClockOutcome } from '@/lib/harvestCapture'
import { compressStandard, safeCompress, tenantPath, tenantPathResolved, storageFrom, uploadWithTimeout, isAlreadyUploaded } from '@/lib/storage'
import { isUniqueViolation, ValidationError, reportError } from '@/lib/errorReporter'
import { FARM_LOAD_REFUSED } from '@/lib/farmLoads'
import { offline, isNetworkish, isAuthTransient, isPermanent4xx, isPpeRefusal, isRlsDenial, isServerBusy, retryAfterMs, operatorStrandMessage } from '@/lib/offlineQueue/classify'
import { decodeStoredClaims, hasStoredSession } from '@/lib/offlineSession'
// Drain-time planting-degrade dialog (mig 20261016222316; lib→Dialogs
// precedent: qcRelease.ts).
import { alertDialog } from '@/components/Dialogs'
import { updateWithVersion, isStaleWriteError } from '@/lib/optimistic'
import { PLANTING_DEGRADED_MESSAGE, plantingDegradedMessage } from '@/lib/plantings'
import { FOLLOWUP_FAILED_NOTE } from '@/lib/sprayPartDone'
import { alreadyRecordedFrom, alreadyRecordedMessage, ALREADY_RECORDED_TITLE, type AlreadyRecorded } from '@/lib/sprayAlreadyRecorded'

// Transient fault on a live attempt or a drain send — queue / keep / back off,
// never surface to the operator. Network blips AND the just-reconnected
// expired-JWT case (2026-06-12: a scout ended a walk seconds after regaining
// signal; the live insert went out with the not-yet-refreshed token and
// "JWT expired" hit a hard alert instead of the queue — the token refreshes
// moments later, so it's retry territory exactly like a network blip; the
// drain's main-client send picks up the refreshed token on the next pass), AND
// server-busy / rate-limit / retryable-infra (5xx, PT429) — W5, offline audit.
// The `code` is the SQLSTATE when the caller has it (drain sites pass it after
// W8's dbError preserves it); message-only callers still catch the common cases.
const isTransientFault = (msg: string, code?: string | null): boolean =>
  isNetworkish(msg) || isAuthTransient(msg) || isServerBusy(msg, code)

// Re-throw a supabase-js error as a real Error that KEEPS its SQLSTATE `.code`
// (W8, offline audit). The drain classifies permanent-vs-transient partly by
// code (isPermanent4xx / isServerBusy), but the helper inserts below re-threw
// via `new Error(error.message)`, dropping `.code` — so a helper-routed check
// -constraint RAISE (23514) or a rate-limit (PT429) reached the drain's generic
// catch with code=undefined and fell back to substring-only matching, looping
// to the retry cap instead of failing fast / backing off. Preserve it.
function dbError(error: { message: string; code?: string | null; details?: string | null }): Error {
  const e = new Error(error.message) as Error & { code?: string | null; details?: string | null }
  e.code = error.code ?? null
  // The rate limiter's "Retry after ~N s" hint lives in the error DETAIL, not
  // the message ('rate_limited') — carry it so the drain backoff can honour it.
  e.details = error.details ?? null
  return e
}
// Entry data model lives in ./offlineQueue/types (extracted Phase 3b step 2).
// Imported for internal use + re-exported so '@/lib/offlineQueue' importers of
// these types keep working.
import type {
  Entry, BaseEntry, EntryKind, TippingEntry, CoolingBatchEntry, CoolingTransitionEntry, BatchTipEntry, PalletEntry, PalletReceiptEntry, ScoutingEntry, ScoutingExtraPhoto, ScoutWalkEntry, SecurityIncidentEntry, SecurityIncidentPhoto, EquipmentServiceEntry, VehicleTripEntry, FieldOperationEntry, FarmProblemEntry, TrapVisitEntry, TrapVisitPhoto,
  FertEntry, FertCompleteEntry, IrrigationEntry, DrainReadingEntry, FarmLoadEntry, PickEntry, PickTalliesEntry, PackoutDropsEntry, SprayEntry, SprayCompleteEntry, PhenologyEntry, RejectEntry,
  QcResponseInQueue, QcEntry, DeliveryEntry, FuelEntry, SecurityPatrolScanEntry, LabourAttestEntry, LabourAdjustEntry, StockMovementEntry, StockCountLineEntry, SpanClockEntry, HarvestLoadEntry,
  SprayCompleteData, SprayRecipeLine, SprayBlockTarget,
} from '@/lib/offlineQueue/types'
export type * from '@/lib/offlineQueue/types'
// Change subscription moved to ./offlineQueue/events (Phase 3b step 3).
import { notifyChanged } from '@/lib/offlineQueue/events'
export { onQueueChanged } from '@/lib/offlineQueue/events'
// IDB storage layer moved to ./offlineQueue/db (Phase 3b step 4).
import { withStore, deleteEntry, recordFailure, markFailed, listPending, hardAttempts } from '@/lib/offlineQueue/db'
import { newUuid } from '@/lib/uuid'
import { trapVisitPhotoKey } from '@/lib/trapPhotos'
import { SECURITY_PHOTO_BUCKET, incidentQueuedPhotoKey } from '@/lib/securityPhotoKey'
import { throwFriendly } from '@/lib/friendlyError'
import { patchDeliveryAtCurrentVersion, withPhotoLossNote } from '@/lib/gateIntakeFollowUp'
export { listPending, pendingCount, pendingBreakdown } from '@/lib/offlineQueue/db'
export type { QueueBreakdown } from '@/lib/offlineQueue/db'
import { packoutTotalsOf, type PackoutDayTotal, type PackoutSaveResult } from '@/lib/packerSaveReceipt'


// ---------------------------------------------------------------------------
// Public — enqueue + count + drain
// ---------------------------------------------------------------------------
export async function enqueueTipping(payload: Record<string, unknown>, opts?: { linkRun?: boolean }): Promise<void> {
  const row: TippingEntry = {
    id:           newUuid(),
    kind:         'tipping',
    ...(opts?.linkRun ? { link_run_delivery: true } : {}),
    payload,
    attempted_at: new Date().toISOString(),
    attempt_count: 0,
    last_error:   null,
    failed:       false,
    queued_at:    new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

// FCX-16 — the crate-receive → build-dolly insert (Precool). Same shape as
// tipping: a single-row INSERT that must not hard-fail at the intake dock
// (a Wi-Fi dead zone). Module-local: callers go through
// insertOrQueueCoolingBatch.
async function enqueueCoolingBatch(payload: Record<string, unknown>): Promise<void> {
  const stamp = new Date().toISOString()
  await withStore('readwrite', s => s.add({
    id: newUuid(), kind: 'cooling_batch', payload,
    attempted_at: stamp, queued_at: stamp,
    attempt_count: 0, last_error: null, failed: false,
  } satisfies CoolingBatchEntry))
  notifyChanged()
}

// Berry flow Tier 3 (mig 20260730070407) — the exception to the old
// "status transitions aren't queued" policy, and a deliberate one: the
// cooling state machine is the single arbiter of legal transitions, so a
// stale replay can't corrupt anything — it can only fail loudly and surface
// in the badge. The entry carries the CAPTURED time; the RPC stamps it (the
// register shows when the probe happened, not when Wi-Fi came back).
async function enqueueCoolingTransition(batchId: string, args: Record<string, unknown>): Promise<void> {
  const stamp = new Date().toISOString()
  await withStore('readwrite', s => s.add({
    id: newUuid(), kind: 'cooling_transition', batch_id: batchId, args,
    attempted_at: stamp, queued_at: stamp,
    attempt_count: 0, last_error: null, failed: false,
  } satisfies CoolingTransitionEntry))
  notifyChanged()
}

async function enqueueBatchTip(batchId: string, args: Record<string, unknown>): Promise<void> {
  const stamp = new Date().toISOString()
  await withStore('readwrite', s => s.add({
    id: newUuid(), kind: 'batch_tip', batch_id: batchId, args,
    attempted_at: stamp, queued_at: stamp,
    attempt_count: 0, last_error: null, failed: false,
  } satisfies BatchTipEntry))
  notifyChanged()
}

// External-pallet receipt captured at the gate (Tier 3, mig 20260807090000).
// The RPC is idempotent on the client-minted receipt_id, so a lost-response
// replay early-returns instead of double-receiving. Numbers (RCV-…, pallet
// ids) are minted server-side at drain time.
async function enqueuePalletReceipt(receiptId: string, args: Record<string, unknown>): Promise<void> {
  const stamp = new Date().toISOString()
  await withStore('readwrite', s => s.add({
    id: newUuid(), kind: 'pallet_receipt', receipt_id: receiptId, args,
    attempted_at: stamp, queued_at: stamp,
    attempt_count: 0, last_error: null, failed: false,
  } satisfies PalletReceiptEntry))
  notifyChanged()
}

export async function enqueuePallet(input: Omit<PalletEntry, 'id' | 'kind' | 'attempted_at' | 'attempt_count' | 'last_error' | 'queued_at' | 'failed'>): Promise<void> {
  const row: PalletEntry = {
    id:           newUuid(),
    kind:         'pallet',
    ...input,
    attempted_at: new Date().toISOString(),
    attempt_count: 0,
    last_error:   null,
    failed:       false,
    queued_at:    new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}


// Operator-facing version of deleteEntry — exported so the offline-queue
// badge UI can offer a "clear this stuck item" button. Calls
// notifyChanged() so any open list refreshes immediately.
//
// Use case: a queued entry whose payload is broken (wrong tenant_id,
// since-removed column, etc.) will keep failing every sync attempt
// without operator intervention. The "clear" button discards the
// IndexedDB row so the operator can re-capture clean and move on.
//
// Important: this DELETES, it doesn't retry. The operator must re-
// enter the underlying data through the normal form. Confirm in the
// caller before invoking.
export async function discardQueueEntry(id: string): Promise<void> {
  await deleteEntry(id)
  notifyChanged()
}

// Council T3.8 follow-up — let an operator manually retry an entry the drainer
// gave up on (marked `failed` after a permanent-looking fault or the retry
// cap). Clears the failed flag + resets the attempt counter so the next drain
// picks it up again, then kicks a drain. Useful when the cause was actually
// fixable after the fact — a role corrected, a since-freed unique value, a
// since-restored FK, or just a long outage that blew past the retry cap.
export async function retryQueueEntry(id: string): Promise<void> {
  const entry = await withStore<Entry | undefined>('readonly', s => s.get(id) as IDBRequest<Entry | undefined>)
  if (!entry) return
  await rearm([entry])
  notifyChanged()
  // Operator override — an explicit retry must not be swallowed by an active
  // W6 backoff window armed by the automatic drains.
  clearDrainBackoff()
  if (navigator.onLine) void drainQueue()
}

// Re-arm entries for the next drain. Both counters reset: the operator's tap
// says "the cause is fixed", so the entry deserves a clean budget, not the
// remains of the one it burned before.
async function rearm(entries: Entry[]): Promise<void> {
  for (const entry of entries) {
    entry.failed        = false
    entry.attempt_count = 0
    entry.hard_attempts = 0
    entry.last_error    = null
    await withStore('readwrite', s => s.put(entry))
  }
}

// Re-arm EVERY failed entry in one go. A server-side fix (a corrected role, a
// re-pointed badge, a freed unique value) heals many stranded writes at once,
// but nothing on the device notices: `failed` entries are skipped by every
// automatic drain AND by "Sync now", so each one needed its own tap to come
// back. That asymmetry is how a fixed bug still reads as lost data in the field
// (Anchor 2026-07-27 — a picker's pay sat behind three separate taps nobody
// knew to make). Returns how many were re-armed so the caller can say so.
export async function retryAllFailed(): Promise<number> {
  const failed = (await listPending()).filter(e => e.failed)
  if (!failed.length) return 0
  await rearm(failed)
  notifyChanged()
  clearDrainBackoff()
  if (navigator.onLine) void drainQueue()
  return failed.length
}


// ---------------------------------------------------------------------------
// Try-or-queue helpers — mutations call these. If the write succeeds
// immediately, returns 'sent'. If the network fails, queues + returns
// 'queued' so the UI can show a hint instead of a hard error.
// ---------------------------------------------------------------------------
// Shared engine for single-row try-or-queue kinds (tipping, cooling_batch):
// live attempt with timeout; transient fault → queue; PK collision → the row
// already committed on a prior lost-response attempt (the caller's
// client-minted id makes the retry idempotent) → success; anything else
// throws with its SQLSTATE preserved.
// One branch per fault class — the classification IS the function.
// fallow-ignore-next-line complexity
async function insertOrQueueRow(
  table: string,
  row: Record<string, unknown>,
  enqueue: (row: Record<string, unknown>) => Promise<void>,
  label: string,
  // The live send, when it is more than one plain insert (a Yard tip links the
  // run first). Resolves { error } like an insert; defaults to the insert.
  send: () => PromiseLike<{ error: { message: string; code?: string | null; details?: string | null } | null }>
    = () => supabase.from(table).insert(row),
): Promise<'sent' | 'queued'> {
  if (offline() || !(await supabaseReachable())) {
    await enqueue(row)
    return 'queued'
  }
  try {
    const { error } = await withTimeout(
      send(),
      LIVE_ATTEMPT_TIMEOUT_MS,
      label,
    )
    if (error) {
      if (isTransientFault(error.message)) {
        await enqueue(row)
        return 'queued'
      }
      if (isUniqueViolation(error)) return 'sent'
      throw dbError(error)
    }
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueue(row)
      return 'queued'
    }
    throw e
  }
}

// W1 (offline audit 2026-06-30) — idempotency hinges on a CLIENT-minted
// tipping_id: a lost-response live attempt + a queued replay must collide on
// the same PK (23505 → treated as success) instead of minting two tips
// (tipping_log.tipping_id DEFAULTs gen_random_uuid() server-side, so an
// id-less insert double-counts bins+kg into the run). Tipping.tsx mints one;
// the Yard "tip extras" path did NOT (Yard.tsx:372). Default it here so every
// caller — present and future — is idempotent even if it forgets. An explicit
// id in the payload still wins.
//
// `linkRun` (the Yard's Tip extras): also link the delivery to the run in
// run_deliveries, ahead of the tip, on the live attempt AND on a queued replay.
// The Tipping page only tips deliveries a run already holds; the Yard tips any
// in-yard delivery, and without the link the board kept showing "Unallocated"
// and the Tipping page never listed the rest of that delivery.
export async function insertOrQueueTipping(
  payload: Record<string, unknown>, opts?: { linkRun?: boolean },
): Promise<'sent' | 'queued'> {
  const row = { tipping_id: newUuid(), ...payload }
  if (!opts?.linkRun) return insertOrQueueRow('tipping_log', row, enqueueTipping, 'tipping insert')
  return insertOrQueueRow('tipping_log', row, r => enqueueTipping(r, { linkRun: true }), 'tipping insert',
    () => sendTip(row, true))
}

// One tip, optionally preceded by the run ↔ delivery link. The link is
// idempotent: run_deliveries' primary key is (run_id, delivery_id), so a 23505
// means it was already there (linked on the Runs page, or by a replay whose
// response was lost) and the tip goes ahead. Any other link error is returned
// as the send's error and classified like a failed tip, so nothing is tipped
// onto a run the delivery could not be linked to.
async function sendTip(
  row: Record<string, unknown>, linkRun: boolean,
): Promise<{ error: { message: string; code?: string | null; details?: string | null } | null }> {
  if (linkRun) {
    const { error } = await supabase.from('run_deliveries')
      .insert({ run_id: row.run_id, delivery_id: row.delivery_id })
    if (error && !isUniqueViolation(error)) return { error }
  }
  return await supabase.from('tipping_log').insert(row)
}

// FCX-16 — same idempotency story on batch_id. The mig-823 availability
// trigger stays the hard gate — a drain replay after the delivery's crates
// were consumed elsewhere fails permanent (23514) and surfaces as a stranded
// write, never a silent duplicate.
export async function insertOrQueueCoolingBatch(payload: Record<string, unknown>): Promise<'sent' | 'queued'> {
  const row = { batch_id: newUuid(), ...payload }
  return insertOrQueueRow('cooling_batches', row, enqueueCoolingBatch, 'cooling batch insert')
}

// Tier 3 — live-attempt-then-queue for an RPC, mirroring insertOrQueueRow.
// Lost-response replays are safe by construction: a transition replay whose
// first attempt actually landed hits the same-status benign-edit path (all
// timestamps frozen by the trigger's reset block), and a tip replay by the
// same person into the same run gets the tip that landed back (mig
// 20261032950000, BF-01) — anyone else still meets the batch-must-be-RELEASED
// check. Never a double-count.
async function rpcOrQueue(
  fn: string,
  args: Record<string, unknown>,
  enqueue: () => Promise<void>,
  label: string,
): Promise<'sent' | 'queued'> {
  if (offline() || !(await supabaseReachable())) {
    await enqueue()
    return 'queued'
  }
  try {
    const { error } = await withTimeout(supabase.rpc(fn, args), LIVE_ATTEMPT_TIMEOUT_MS, label)
    if (error) {
      if (isTransientFault(error.message, (error as { code?: string | null }).code)) {
        await enqueue()
        return 'queued'
      }
      throw dbError(error)
    }
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueue()
      return 'queued'
    }
    throw e
  }
}

/** Cool / hold / release with the operator's capture time (Tier 3). */
export async function applyOrQueueCoolingTransition(
  batchId: string,
  args: Record<string, unknown>,
): Promise<'sent' | 'queued'> {
  const full = { p_batch_id: batchId, ...args }
  return rpcOrQueue('apply_cooling_transition', full,
    () => enqueueCoolingTransition(batchId, full), 'cooling transition')
}

/** Tip a released dolly into a run, offline-tolerant (Tier 3). */
export async function tipOrQueueBatch(
  batchId: string,
  args: Record<string, unknown>,
): Promise<'sent' | 'queued'> {
  const full = { p_batch_id: batchId, ...args }
  return rpcOrQueue('tip_cooling_batch', full,
    () => enqueueBatchTip(batchId, full), 'batch tip')
}

/** Receive externally-packed pallets at the gate, offline-tolerant (Tier 3).
 *  args must already carry p_receipt_id (client-minted UUID — the
 *  idempotency key) plus the receive_packed_pallets params. */
export async function receiveOrQueuePalletReceipt(
  receiptId: string,
  args: Record<string, unknown>,
): Promise<'sent' | 'queued'> {
  const full = { p_receipt_id: receiptId, ...args }
  return rpcOrQueue('receive_packed_pallets', full,
    () => enqueuePalletReceipt(receiptId, full), 'pallet receipt')
}

export interface QueuePalletInput {
  run_id:    string
  grower:    string
  variety:   string
  puc:       string | null
  packed_by: string
  fields:    Record<string, unknown>          // pallet column values minus pallet_id + token_id
  photo:     Blob | null
  photo_name: string | null
  token_id?: string                           // minted by insertOrQueuePallet for idempotency
}

// ---------------------------------------------------------------------------
// Scouting — same pattern as tipping (plain insert), plus optional photo
// blob held locally and uploaded on drain.
// ---------------------------------------------------------------------------

/**
 * The tenant a queued farm row BELONGS to, read off the payload it already
 * carries. Lifting it to the entry is what lets the drain defer instead of
 * uploading a photo into, and then being refused by, a different tenant.
 * Null when the caller did not stamp one — those entries drain best-effort,
 * exactly as pre-guard spray entries do.
 */
function originTenantOf(payload: Record<string, unknown>): string | null {
  const t = payload.tenant_id
  return typeof t === 'string' && t ? t : null
}

export async function enqueueScouting(input: { payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null; extra_photos?: ScoutingExtraPhoto[] }): Promise<void> {
  const row: ScoutingEntry = {
    id:           newUuid(),
    kind:         'scouting',
    payload:      input.payload,
    // The row already carries its tenant; lift it to the ENTRY so the drain
    // can read it without parsing a payload, the way the spray guard does.
    tenant_id:    originTenantOf(input.payload),
    photo:        input.photo,
    photo_name:   input.photo_name,
    extra_photos: input.extra_photos ?? [],
    attempted_at: new Date().toISOString(),
    attempt_count: 0,
    last_error:   null,
    failed:       false,
    queued_at:    new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

export async function insertOrQueueScouting(input: { payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null; extra_photos?: ScoutingExtraPhoto[] } & { db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  // Mint the PK once, up front, so the live attempt AND any queued retry write
  // the SAME observation_id — a lost-response live insert then a drain retry
  // dedupe via upsert-ignore (runScoutingInsert) instead of duplicating.
  if (!input.payload.observation_id) input.payload.observation_id = newUuid()
  if (offline() || !(await supabaseReachable())) {
    await enqueueScouting(input)
    return 'queued'
  }
  try {
    await withTimeout(runScoutingInsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'scouting insert')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueScouting(input)
      return 'queued'
    }
    // A live RLS denial = this account can't log scouting observations (wrong
    // role) or its session tenant is stale. Operator/admin setup, not a code
    // bug — re-throw as a ValidationError so the field user gets an actionable
    // message and Sentry isn't paged. (Caught 2026-06-04: a FARM member left on
    // the packhouse INSPECTOR role hit the raw "violates row-level security"
    // string + a Sentry page when their scout couldn't save.)
    if (isRlsDenial(msg)) {
      throw new ValidationError(
        "You don't have permission to log scouting observations. Pull to refresh; "
        + "if it keeps failing, ask a farm admin to set your role to Scout (or higher).",
      )
    }
    throw e
  }
}

// ---------------------------------------------------------------------------
// scout_walk — GPS polyline, pure log, no photo. Idempotent on walk_id.
// ---------------------------------------------------------------------------
export async function enqueueScoutWalk(payload: Record<string, unknown>): Promise<void> {
  const row: ScoutWalkEntry = {
    id:            newUuid(),
    kind:          'scout_walk',
    payload,
    tenant_id:     originTenantOf(payload),
    attempted_at:  new Date().toISOString(),
    attempt_count: 0,
    last_error:    null,
    failed:        false,
    queued_at:     new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

async function runScoutWalkInsert(input: { payload: Record<string, unknown>; db?: SupabaseClient }): Promise<void> {
  const db = input.db ?? supabase
  // Upsert-ignore on the client-minted walk_id so a lost-response replay is a
  // 0-row no-op rather than a duplicate track.
  const { error } = await db.from('scout_walks')
    .upsert(input.payload, { onConflict: 'walk_id', ignoreDuplicates: true })
  if (error) throw dbError(error)
}

// ── Farm Problems (docs/specs/farm-problems.md) ─────────────────────────────
// Same contract as scouting: mint the PK up front so a lost-response live
// insert and its queued replay dedupe via upsert-ignore on problem_id.
async function enqueueFarmProblem(input: { payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null }): Promise<void> {
  const row: FarmProblemEntry = {
    id:            newUuid(),
    kind:          'farm_problem',
    payload:       input.payload,
    tenant_id:     originTenantOf(input.payload),
    photo:         input.photo,
    photo_name:    input.photo_name,
    attempted_at:  new Date().toISOString(),
    attempt_count: 0,
    last_error:    null,
    failed:        false,
    queued_at:     new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

export async function insertOrQueueFarmProblem(input: { payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null } & { db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  if (!input.payload.problem_id) input.payload.problem_id = newUuid()
  if (offline() || !(await supabaseReachable())) {
    await enqueueFarmProblem(input)
    return 'queued'
  }
  try {
    await withTimeout(runFarmProblemInsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'farm problem insert')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueFarmProblem(input)
      return 'queued'
    }
    if (isRlsDenial(msg)) {
      throw new ValidationError(
        "You don't have permission to report farm problems on this tenant. Pull to refresh; "
        + "if it keeps failing, ask a farm admin to check your role.",
      )
    }
    throw e
  }
}

async function runFarmProblemInsert(input: { payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null; db?: SupabaseClient; photoTries?: number }): Promise<void> {
  await runWithPhotoInsert({
    db:         input.db,
    photoTries: input.photoTries,
    table:      'farm_problems',
    bucket:     'scouting-photos',
    dateField:  'reported_on',
    payload:    input.payload,
    photo:      input.photo,
    photo_name: input.photo_name,
    conflictColumn: 'problem_id',
  })
}

// ── Traps (mig 20261038600000) ─────────────────────────────────────────────
// A service visit to a pest trap. Same contract as a farm problem — mint the
// PK up front so a lost-response live insert and its queued replay dedupe via
// upsert-ignore on visit_id — with up to six photos instead of one.
/** The bookkeeping half of a brand-new entry — the fields every kind starts
 *  with. New kinds build on this instead of spelling the six out again. */
function freshEntry<K extends EntryKind>(kind: K) {
  const now = new Date().toISOString()
  return { id: newUuid(), kind, attempted_at: now, attempt_count: 0, last_error: null, failed: false, queued_at: now }
}

async function enqueueTrapVisit(input: { payload: Record<string, unknown>; photos: TrapVisitPhoto[] }): Promise<void> {
  const row: TrapVisitEntry = {
    ...freshEntry('trap_visit'),
    payload: input.payload, tenant_id: originTenantOf(input.payload), photos: input.photos,
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

export async function insertOrQueueTrapVisit(input: { payload: Record<string, unknown>; photos: TrapVisitPhoto[] } & { db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  if (!input.payload.visit_id) input.payload.visit_id = newUuid()
  if (offline() || !(await supabaseReachable())) {
    await enqueueTrapVisit(input)
    return 'queued'
  }
  try {
    await withTimeout(runTrapVisitInsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'trap visit insert')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueTrapVisit(input)
      return 'queued'
    }
    if (isRlsDenial(msg)) {
      throw new ValidationError(
        "You don't have permission to log trap visits on this farm. Pull to refresh; "
        + 'if it keeps failing, ask a farm admin to check your role.',
      )
    }
    throw e
  }
}

/**
 * Upload each photo, then upsert the visit carrying the paths that landed.
 * The posture is runWithPhotoInsert's: a photo that fails to upload costs
 * that photo, never the visit — the count is what the trap round exists for,
 * and nobody can be sent back to the trap hours later. Each object is keyed
 * on the visit's and the photo's client-minted ids (trapVisitPhotoKey), so a
 * replay overwrites its own upload; paths come from the ROW's tenant, never
 * the live JWT (the drain may run under another tenant — see
 * runWithPhotoInsert's note on why that ordering matters).
 */
async function runTrapVisitInsert(input: { payload: Record<string, unknown>; photos: TrapVisitPhoto[]; db?: SupabaseClient }): Promise<void> {
  const db = input.db ?? supabase
  const visitId = input.payload.visit_id as string
  const trapId = input.payload.trap_id as string
  const rowTenant = typeof input.payload.tenant_id === 'string' ? input.payload.tenant_id : null
  const uploaded = await Promise.all((input.photos ?? []).map(async (p) => {
    try {
      const compressed = await compressStandard(p.blob as File)
      const key = trapVisitPhotoKey(trapId, visitId, p.photo_id, p.photo_name)
      const path = rowTenant ? tenantPath(rowTenant, key) : await tenantPathResolved(key)
      const { error } = await uploadWithTimeout('scouting-photos', path, compressed,
        { upsert: true, contentType: p.blob.type || 'image/jpeg' }, undefined, db)
      if (error) throw error
      return path
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[queue] a trap visit photo failed to upload; the visit and its other photos are unaffected', e)
      return null
    }
  }))
  const row = { ...input.payload, photo_paths: uploaded.filter((p): p is string => p !== null) }
  const { error } = await db.from('farm_trap_visits')
    .upsert(row, { onConflict: 'visit_id', ignoreDuplicates: true })
  if (error) throw dbError(error)
}

// ── Security incidents (FM10 UF-23) ────────────────────────────────────────
// A guard at the far fence has no signal. The incident row and its photos are
// kept on the phone and sent once. Idempotent end to end: the row is an
// upsert-ignore on the client-minted incident_id (F6), each photo object is
// keyed on its own client-minted id, and its timeline row is looked up before
// it is inserted, so a replay after a half-sent attempt adds nothing twice.
async function enqueueSecurityIncident(input: { payload: Record<string, unknown>; photos: SecurityIncidentPhoto[] }): Promise<void> {
  const row: SecurityIncidentEntry = {
    ...freshEntry('security_incident'),
    payload: input.payload, tenant_id: originTenantOf(input.payload), photos: input.photos,
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

/** Row first (the timeline rows reference it), then each photo. A photo that
 *  fails THROWS: the incident has landed, the replay is idempotent, and a photo
 *  is evidence, so it is retried rather than dropped.
 *
 *  Replay-safe without any lookup: the object path is keyed on the photo's id and
 *  uploaded with upsert:false (the bucket has no UPDATE policy by design), so an
 *  object that is already there IS this photo (isAlreadyUploaded). The timeline
 *  row's event_id IS the photo's id, so two racing runs hit the primary key and
 *  the second is ignored: never two rows for one photo.
 *
 *  `ctl.cancelled` is set when the live attempt times out and the entry is queued
 *  instead: the abandoned attempt stops before its next upload or insert, so it
 *  cannot run beside the drain. */
async function sendIncidentPhoto(db: SupabaseClient, incidentId: string, rowTenant: string | null, createdBy: string | null, p: SecurityIncidentPhoto, stop: () => void): Promise<void> {
  const key = incidentQueuedPhotoKey(incidentId, p.photo_id)
  const path = rowTenant ? tenantPath(rowTenant, key) : await tenantPathResolved(key)
  const up = await uploadWithTimeout(SECURITY_PHOTO_BUCKET, path, p.blob,
    { upsert: false, contentType: 'image/jpeg' }, undefined, db)
  if (up?.error && !isAlreadyUploaded(up.error)) throw dbError(up.error)
  stop()
  const ev = await db.from('security_incident_events')
    .upsert({ event_id: p.photo_id, incident_id: incidentId, event_type: 'PHOTO', photo_url: path, ...(createdBy ? { created_by: createdBy } : {}) },
      { onConflict: 'event_id', ignoreDuplicates: true })
  if (ev.error) throw dbError(ev.error)
}

async function runSecurityIncidentInsert(input: {
  payload: Record<string, unknown>; photos: SecurityIncidentPhoto[]; db?: SupabaseClient
  onRowIn?: () => void; onPhotoDone?: (photoId: string) => void; ctl?: { cancelled: boolean }
}): Promise<void> {
  const db = input.db ?? supabase
  const incidentId = input.payload.incident_id as string
  const rowTenant = typeof input.payload.tenant_id === 'string' ? input.payload.tenant_id : null
  // The photo's timeline row is credited to whoever captured it, like the row itself.
  const createdBy = typeof input.payload.created_by === 'string' ? input.payload.created_by : null
  const stop = () => { if (input.ctl?.cancelled) throw new Error('security incident attempt cancelled') }
  stop()
  const { error } = await db.from('security_incidents')
    .upsert(input.payload, { onConflict: 'incident_id', ignoreDuplicates: true })
  if (error) throw dbError(error)
  input.onRowIn?.()
  for (const p of input.photos ?? []) {
    stop()
    await sendIncidentPhoto(db, incidentId, rowTenant, createdBy, p, stop)
    input.onPhotoDone?.(p.photo_id)
  }
}

/** Photos are compressed BEFORE they are queued, so an IndexedDB full of raw camera
 *  frames cannot run the phone out of quota. A photo that cannot be processed is
 *  dropped (and reported), never the whole incident. */
async function compressIncidentPhotos(photos: SecurityIncidentPhoto[], onDropped?: (photoId: string) => void): Promise<SecurityIncidentPhoto[]> {
  const out = await Promise.all((photos ?? []).map(async p => {
    try { return { ...p, blob: await safeCompress(p.blob as File) } }
    catch { onDropped?.(p.photo_id); return null }
  }))
  return out.filter((p): p is SecurityIncidentPhoto => p !== null)
}

export async function insertOrQueueSecurityIncident(input: { payload: Record<string, unknown>; photos: SecurityIncidentPhoto[] } & { db?: SupabaseClient; onRowIn?: () => void; onPhotoDone?: (photoId: string) => void; onPhotoDropped?: (photoId: string) => void }): Promise<'sent' | 'queued'> {
  if (!input.payload.incident_id) input.payload.incident_id = newUuid()
  const photos = await compressIncidentPhotos(input.photos, input.onPhotoDropped)
  const queue = { payload: input.payload, photos }
  if (offline() || !(await supabaseReachable())) {
    await enqueueSecurityIncident(queue)
    return 'queued'
  }
  const ctl = { cancelled: false }
  try {
    await withTimeout(runSecurityIncidentInsert({ ...queue, db: input.db, onRowIn: input.onRowIn, onPhotoDone: input.onPhotoDone, ctl }),
      LIVE_ATTEMPT_TIMEOUT_MS, 'security incident insert')
    return 'sent'
  } catch (e: unknown) {
    ctl.cancelled = true   // the abandoned attempt must not keep writing beside the drain
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueSecurityIncident(queue)
      return 'queued'
    }
    if (isRlsDenial(msg)) {
      throw new ValidationError(
        "You don't have permission to log security incidents here. Pull to refresh; "
        + 'if it keeps failing, ask an admin to check your role.',
      )
    }
    throwFriendly(e)
  }
}

export async function insertOrQueueScoutWalk(input: { payload: Record<string, unknown>; db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  // Mint the PK once so the live attempt AND any queued retry share walk_id.
  if (!input.payload.walk_id) input.payload.walk_id = newUuid()
  if (offline() || !(await supabaseReachable())) {
    await enqueueScoutWalk(input.payload)
    return 'queued'
  }
  try {
    await withTimeout(runScoutWalkInsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'scout walk insert')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueScoutWalk(input.payload)
      return 'queued'
    }
    if (isRlsDenial(msg)) {
      throw new ValidationError(
        "You don't have permission to save scout walks. Pull to refresh; if it "
        + "keeps failing, ask a farm admin to set your role to Scout (or higher).",
      )
    }
    throw e
  }
}

// ---------------------------------------------------------------------------
// equipment_service — single-table insert + optional photo, idempotent on the
// client-minted event_id. EDITS still require being online (updateWithVersion);
// only NEW service events queue.
// ---------------------------------------------------------------------------
export async function enqueueEquipmentService(input: { payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null }): Promise<void> {
  const row: EquipmentServiceEntry = {
    id:            newUuid(),
    kind:          'equipment_service',
    payload:       input.payload,
    photo:         input.photo,
    photo_name:    input.photo_name,
    attempted_at:  new Date().toISOString(),
    attempt_count: 0,
    last_error:    null,
    failed:        false,
    queued_at:     new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

async function runEquipmentServiceInsert(input: { payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null; db?: SupabaseClient; photoTries?: number }): Promise<void> {
  await runWithPhotoInsert({
    db:             input.db,
    photoTries: input.photoTries,
    table:          'farm_equipment_service_events',
    bucket:         'spray-evidence',   // shared bucket (same as fert + spray)
    dateField:      'service_date',
    payload:        input.payload,
    photo:          input.photo,
    photo_name:     input.photo_name,
    conflictColumn: 'event_id',
  })
}

export async function insertOrQueueEquipmentService(input: { payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null; db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  if (!input.payload.event_id) input.payload.event_id = newUuid()
  if (offline() || !(await supabaseReachable())) {
    await enqueueEquipmentService(input)
    return 'queued'
  }
  try {
    await withTimeout(runEquipmentServiceInsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'equipment service insert')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueEquipmentService(input)
      return 'queued'
    }
    if (isRlsDenial(msg)) {
      throw new ValidationError(
        "You don't have permission to log equipment service events. Pull to "
        + "refresh; if it keeps failing, ask a farm admin to check your role.",
      )
    }
    throw e
  }
}

// ---------------------------------------------------------------------------
// fert — fertiliser_applications, one row per target block, shared photo.
// Idempotent on each row's application_id (no double stock decrement on replay).
// ---------------------------------------------------------------------------
export async function enqueueFert(input: { rows: Record<string, unknown>[]; photo: Blob | null; photo_name: string | null }): Promise<void> {
  const row: FertEntry = {
    id:            newUuid(),
    kind:          'fert',
    rows:          input.rows,
    photo:         input.photo,
    photo_name:    input.photo_name,
    attempted_at:  new Date().toISOString(),
    attempt_count: 0,
    last_error:    null,
    failed:        false,
    queued_at:     new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

async function runFertInsert(input: { rows: Record<string, unknown>[]; photo: Blob | null; photo_name: string | null; db?: SupabaseClient }): Promise<void> {
  const db = input.db ?? supabase
  if (!input.rows.length) return
  // Upload the one shared evidence photo, keyed on the FIRST row's minted
  // application_id so a replay reuses the same object. Photo failure drops the
  // photo so the rows still land (we can't re-prompt the operator hours later).
  let photoPath: string | null = (input.rows[0].photo_url as string | null) ?? null
  if (input.photo) {
    try {
      const compressed = await compressStandard(input.photo as File)
      const ext     = (input.photo_name?.split('.').pop() ?? 'jpg').toLowerCase()
      const dateKey = (input.rows[0].applied_at as string) ?? sastTodayYMD()
      const blockId = (input.rows[0].block_id as string) ?? 'no-block'
      const fileKey = (input.rows[0].application_id as string) ?? String(Date.now())
      const path    = await tenantPathResolved(`fert/${dateKey}/${blockId}/${fileKey}.${ext}`)
      const { error: upErr } = await storageFrom('spray-evidence', db)
        .upload(path, compressed, { upsert: true, contentType: input.photo.type || 'image/jpeg' })
      if (upErr) throw upErr
      photoPath = path
    } catch (uploadErr) {
      // eslint-disable-next-line no-console
      console.warn('fertiliser_applications photo upload failed; inserting without photo', uploadErr)
    }
  }
  const rows = input.rows.map(r => ({ ...r, photo_url: photoPath }))
  const { error } = await db.from('fertiliser_applications')
    .upsert(rows, { onConflict: 'application_id', ignoreDuplicates: true })
  if (error) throw dbError(error)
}

// ---------------------------------------------------------------------------
// fert_complete — "Record as done" on an agent's planned fertiliser row, online
// OR offline (Jason, 1 Oct). Always the same version-guarded UPDATE of that row
// (PLANNED → COMPLETED), never an insert, so the stock comes off once. A replay
// against a row that changed since is the guard: if it was recorded meanwhile
// (by anyone, or by our own lost-response attempt) nothing more is written; if
// it was rejected or edited, the record is refused and the operator told.
// ---------------------------------------------------------------------------
export type FertCompleteData = {
  application_id:   string
  expected_version: number
  patch:            Record<string, unknown>
  photo:            Blob | null
  photo_name:       string | null
}

/** The row changed (rejected, edited, deleted) since this record was made: not applied. */
/**
 * A find landed, but its extra photos were REFUSED (RLS 42501). Since mig
 * 20261036100000 a scout or foreman may add a photo only to a find THEY logged,
 * so a find that landed under one sign-in and whose photos drain under another
 * (a shared phone, a sync that stopped half-way) is refused — and retrying as
 * the same person never heals it. The entry is KEPT as a failed item with this
 * sentence, never dropped behind a console.warn; Retry works once the person
 * who logged the find is signed in, and discarding it drops only the photos.
 * The wording avoids the classifiers' trigger words (no "permission denied",
 * "network", "timeout"), so it is never re-routed as an RLS or transient fault.
 */
export class ScoutPhotosRefused extends Error {
  constructor() {
    super('This find was saved, but its extra photos were not: only the person who logged a find can add photos to it. '
      + 'Sign in as that person and tap Retry, or discard this item to drop the photos (the find stays).')
    this.name = 'ScoutPhotosRefused'
  }
}

export class FertCompleteRefused extends Error {
  constructor() {
    super('A fertiliser recommendation recorded as done was NOT applied: it changed after you recorded it '
      + '(rejected, edited or deleted). Open Fertilising, check it, and record it again if it still applies.')
    this.name = 'FertCompleteRefused'
  }
}

export const FERT_ALREADY_RECORDED_SYNC =
  'A fertiliser recommendation you recorded as done had already been recorded when it synced. '
  + 'The record that was already there stands; stock came off once.'

async function enqueueFertComplete(input: FertCompleteData): Promise<void> {
  const row: FertCompleteEntry = {
    id:               newUuid(),
    kind:             'fert_complete',
    application_id:   input.application_id,
    expected_version: input.expected_version,
    patch:            input.patch,
    photo:            input.photo,
    photo_name:       input.photo_name,
    attempted_at:     new Date().toISOString(),
    attempt_count:    0,
    last_error:       null,
    failed:           false,
    queued_at:        new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

// The photo for a Record as done, keyed on the application_id so a replay
// reuses the same object. A failed upload drops the photo, not the record
// (as runFertInsert): returns the storage path, or null.
async function uploadFertCompletePhoto(db: SupabaseClient, input: FertCompleteData, patch: Record<string, unknown>): Promise<string | null> {
  if (!input.photo) return null
  try {
    const compressed = await compressStandard(input.photo as File)
    const ext     = (input.photo_name?.split('.').pop() ?? 'jpg').toLowerCase()
    const dateKey = (patch.applied_at as string) ?? sastTodayYMD()
    const blockId = (patch.block_id as string) ?? 'no-block'
    const path    = await tenantPathResolved(`fert/${dateKey}/${blockId}/${input.application_id}.${ext}`)
    const { error: upErr } = await storageFrom('spray-evidence', db)
      .upload(path, compressed, { upsert: true, contentType: input.photo.type || 'image/jpeg' })
    if (upErr) throw upErr
    return path
  } catch (uploadErr) {
    // eslint-disable-next-line no-console
    console.warn('fert record-as-done photo upload failed; recording without photo', uploadErr)
    return null
  }
}

// The version guard, after a stale write: a row now COMPLETED was recorded
// (by anyone, or by our own attempt whose reply was lost) — nothing more to
// write. Anything else (rejected, edited, deleted) refuses the record.
async function settleStaleFertComplete(db: SupabaseClient, applicationId: string): Promise<{ alreadyRecorded: true }> {
  const { data, error } = await db.from('fertiliser_applications')
    .select('status').eq('application_id', applicationId).is('deleted_at', null).maybeSingle()
  if (error) throw dbError(error)
  if ((data as { status?: string } | null)?.status === 'COMPLETED') return { alreadyRecorded: true }
  throw new FertCompleteRefused()
}

async function runFertComplete(input: FertCompleteData & { db?: SupabaseClient }): Promise<{ alreadyRecorded: boolean }> {
  const db = input.db ?? supabase
  const patch: Record<string, unknown> = { ...input.patch }
  const photoPath = await uploadFertCompletePhoto(db, input, patch)
  if (photoPath) patch.photo_url = photoPath
  try {
    await withTimeout(updateWithVersion('fertiliser_applications',
      { col: 'application_id', value: input.application_id }, input.expected_version, patch, { db }),
      LIVE_ATTEMPT_TIMEOUT_MS, 'fert record as done update')
    return { alreadyRecorded: false }
  } catch (e) {
    if (!isStaleWriteError(e)) throw e
    return settleStaleFertComplete(db, input.application_id)
  }
}

export async function recordOrQueueFertComplete(input: FertCompleteData & { db?: SupabaseClient }): Promise<'sent' | 'queued' | 'already_recorded'> {
  if (offline() || !(await supabaseReachable())) {
    await enqueueFertComplete(input)
    return 'queued'
  }
  try {
    const res = await withTimeout(runFertComplete(input), LIVE_ATTEMPT_TIMEOUT_MS, 'fert record as done')
    return res.alreadyRecorded ? 'already_recorded' : 'sent'
  } catch (e: unknown) {
    if (e instanceof FertCompleteRefused) throw e
    const msg = e instanceof Error ? e.message : String(e)
    // A lost response is safe to queue: the replay meets its own landed write
    // as a version change on a COMPLETED row, and writes nothing more.
    if (isTransientFault(msg)) {
      await enqueueFertComplete(input)
      return 'queued'
    }
    if (isRlsDenial(msg)) {
      throw new ValidationError(
        "You don't have permission to record fertiliser applications. Pull to "
        + "refresh; if it keeps failing, ask a farm admin to check your role.",
      )
    }
    throw e
  }
}

export async function insertOrQueueFert(input: { rows: Record<string, unknown>[]; photo: Blob | null; photo_name: string | null; db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  // Mint an application_id per row up front so the live attempt AND any queued
  // retry write the SAME ids → upsert-ignore dedupes (no double decrement).
  for (const r of input.rows) { if (!r.application_id) r.application_id = newUuid() }
  if (offline() || !(await supabaseReachable())) {
    await enqueueFert(input)
    return 'queued'
  }
  try {
    await withTimeout(runFertInsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'fert insert')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueFert(input)
      return 'queued'
    }
    if (isRlsDenial(msg)) {
      throw new ValidationError(
        "You don't have permission to log fertiliser applications. Pull to "
        + "refresh; if it keeps failing, ask a farm admin to check your role.",
      )
    }
    throw e
  }
}

// Irrigation log (BIGFARM-UX-W3 §4.1) — the fert mechanism verbatim, minus
// the photo: client-minted application_id + upsert-ignore replay, tenant_id
// stamped at ENQUEUE time (security-playbook: the column DEFAULT resolves
// current_tenant_id() at DRAIN, i.e. possibly after a tenant switch).
// Internal like the sibling enqueue* helpers — the public entry is
// insertOrQueueIrrigation; the drain calls runIrrigationInsert directly.
async function enqueueIrrigation(input: { payload: Record<string, unknown> }): Promise<void> {
  const row: IrrigationEntry = {
    id:            newUuid(),
    kind:          'irrigation',
    payload:       input.payload,
    attempted_at:  new Date().toISOString(),
    attempt_count: 0,
    last_error:    null,
    failed:        false,
    queued_at:     new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

async function runIrrigationInsert(input: { payload: Record<string, unknown>; db?: SupabaseClient }): Promise<void> {
  const db = input.db ?? supabase
  const { error } = await db.from('irrigation_applications')
    .upsert([input.payload], { onConflict: 'application_id', ignoreDuplicates: true })
  if (error) throw dbError(error)
}

export async function insertOrQueueIrrigation(input: { payload: Record<string, unknown>; db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  // Mint the PK up front so the live attempt AND any queued retry write the
  // SAME id → upsert-ignore dedupes (a hung request + retry can't double-log
  // the water balance).
  if (!input.payload.application_id) input.payload.application_id = newUuid()
  if (offline() || !(await supabaseReachable())) {
    await enqueueIrrigation(input)
    return 'queued'
  }
  try {
    await withTimeout(runIrrigationInsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'irrigation insert')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueIrrigation(input)
      return 'queued'
    }
    if (isRlsDenial(msg)) {
      throw new ValidationError(
        "You don't have permission to log irrigation. Pull to refresh; if it "
        + 'keeps failing, ask a farm admin to check your role.',
      )
    }
    throw e
  }
}

// Drain / run-off reading (bag-culture blueberries) — the irrigation mechanism
// verbatim: client-minted measurement_id + upsert-ignore replay, tenant_id
// stamped at ENQUEUE time (the column DEFAULT resolves current_tenant_id() at
// DRAIN, i.e. possibly after a tenant switch).
async function enqueueDrainReading(input: { payload: Record<string, unknown> }): Promise<void> {
  const row: DrainReadingEntry = {
    id:            newUuid(),
    kind:          'drain_reading',
    payload:       input.payload,
    attempted_at:  new Date().toISOString(),
    attempt_count: 0,
    last_error:    null,
    failed:        false,
    queued_at:     new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

async function runDrainReadingInsert(input: { payload: Record<string, unknown>; db?: SupabaseClient }): Promise<void> {
  const db = input.db ?? supabase
  const { error } = await db.from('drain_measurements')
    .upsert([input.payload], { onConflict: 'measurement_id', ignoreDuplicates: true })
  if (error) throw dbError(error)
}

// CPB farm→packhouse wagon load (farm_packhouse_loads). Same mechanism as
// irrigation/drain: client-minted load_id + upsert-ignore replay, because the
// lands have no signal and a supervisor must be able to clock a wagon anyway.
//
// ⚠️ The guard DERIVES packhouse_tenant_id server-side, so the queued payload
// deliberately does not carry one — a value stamped at enqueue time would be
// stale if the operator switched tenant before the drain, and the guard would
// overwrite it regardless. Same reasoning as the tenant_id note on drain
// readings, one layer up.
// The queue-entry envelope, built once. Every other enqueue* in this file spells
// these seven fields out inline — ~15 copies of the same literal, which is why
// the duplication gate fires the moment a sixteenth is added.
//
// Deliberately NOT retrofitted onto the existing fifteen: this is the offline
// queue, the one path where a mistake loses a capture that only ever existed on
// someone's phone. Touching every enqueue in a CPB feature branch would be a
// large, unrequested change to the most safety-critical code here. New ones can
// use this; the old ones stay until someone sets out to migrate them on purpose.
function queueEnvelope<K extends EntryKind>(kind: K) {
  const now = new Date().toISOString()
  return {
    id:            newUuid(),
    kind,
    attempted_at:  now,
    attempt_count: 0,
    last_error:    null,
    failed:        false,
    queued_at:     now,
  }
}

// Patrol checkpoint scan — see SecurityPatrolScanEntry for why tenant_id is
// stamped by the CALLER rather than left to the column default.
async function enqueueSecurityPatrolScan(input: { payload: Record<string, unknown> }): Promise<void> {
  const row: SecurityPatrolScanEntry = { ...queueEnvelope('security_patrol_scan'), payload: input.payload }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

async function runSecurityPatrolScanInsert(
  input: { payload: Record<string, unknown>; db?: SupabaseClient },
): Promise<'inserted' | 'duplicate'> {
  const db = input.db ?? supabase
  // Replay-safe: the table's UNIQUE (tenant_id, client_scan_id) is the same key
  // that collapses an imager's double read, so a re-sent scan is a no-op rather
  // than a second visit. ignoreDuplicates (ON CONFLICT DO NOTHING) never raises
  // 23505, so .select() the write and read the RETURNING set: a conflicting
  // scan comes back empty — that is how the caller tells a genuine re-scan
  // ("already recorded · nothing double-counted") from a fresh one (L71).
  const { data, error } = await db.from('security_patrol_scans')
    .upsert([input.payload], { onConflict: 'tenant_id,client_scan_id', ignoreDuplicates: true })
    .select('scan_id')
  if (error) throw dbError(error)
  return (data?.length ?? 0) > 0 ? 'inserted' : 'duplicate'
}

/**
 * Record a patrol scan, queueing it when the write cannot go out.
 *
 * Returns 'sent' or 'queued' so the scan screen can tell the guard which
 * happened — "recorded" and "saved, will send when you have signal" are
 * different facts, and at 02:00 the difference decides whether they walk back.
 */
export async function insertOrQueueSecurityPatrolScan(
  input: { payload: Record<string, unknown>; db?: SupabaseClient },
): Promise<'sent' | 'queued' | 'already recorded'> {
  // Queue without trying when we already know there is nothing to try against.
  // A guard at the far fence is the expected case, not the exception.
  if (offline() || !(await supabaseReachable())) {
    await enqueueSecurityPatrolScan({ payload: input.payload })
    return 'queued'
  }
  try {
    const r = await withTimeout(runSecurityPatrolScanInsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'patrol scan insert')
    // A conflicting scan wrote nothing — the idempotency key working, not a
    // second visit. The scan screen says "already recorded", not a fresh tick.
    return r === 'duplicate' ? 'already recorded' : 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueSecurityPatrolScan({ payload: input.payload })
      return 'queued'
    }
    if (isRlsDenial(msg)) {
      throw new ValidationError(
        "You don't have permission to record patrol scans on this business. Pull to refresh; "
        + 'if it keeps failing, ask an admin to check your role.',
      )
    }
    throw e
  }
}

async function enqueueFarmLoad(input: { payload: Record<string, unknown> }): Promise<void> {
  const row: FarmLoadEntry = { ...queueEnvelope('farm_load'), payload: input.payload }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

async function runFarmLoadInsert(input: { payload: Record<string, unknown>; db?: SupabaseClient }): Promise<void> {
  const db = input.db ?? supabase
  const { error } = await db.from('farm_packhouse_loads')
    .upsert([input.payload], { onConflict: 'load_id', ignoreDuplicates: true })
  if (error) throw dbError(error)
}

export async function insertOrQueueFarmLoad(input: { payload: Record<string, unknown>; db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  if (!input.payload.load_id) input.payload.load_id = newUuid()
  if (offline() || !(await supabaseReachable())) {
    await enqueueFarmLoad(input)
    return 'queued'
  }
  try {
    await withTimeout(runFarmLoadInsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'farm load insert')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueFarmLoad(input)
      return 'queued'
    }
    if (isRlsDenial(msg)) {
      throw new ValidationError(FARM_LOAD_REFUSED)
    }
    throw e
  }
}

export async function insertOrQueueDrainReading(input: { payload: Record<string, unknown>; db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  if (!input.payload.measurement_id) input.payload.measurement_id = newUuid()
  if (offline() || !(await supabaseReachable())) {
    await enqueueDrainReading(input)
    return 'queued'
  }
  try {
    await withTimeout(runDrainReadingInsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'drain reading insert')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueDrainReading(input)
      return 'queued'
    }
    if (isRlsDenial(msg)) {
      throw new ValidationError(
        "You don't have permission to log drain readings. Pull to refresh; if "
        + 'it keeps failing, ask a farm admin to check your role.',
      )
    }
    throw e
  }
}

// runWithPhotoInsert — generic "upload photo to bucket then INSERT row"
// path used by the three farm-app inserts that share this shape (scouting,
// pick, spray). Photo failure drops the photo so the row still lands —
// we can't re-prompt the operator hours later. The path key is built
// from the row's date column and block_id; if either is missing, we fall
// back to today + the entry's id.
const PHOTO_HOLD_PASSES = 3

type PhotoInsertInput = {
  db?:        SupabaseClient
  table:       string
  bucket:      string
  dateField:   string             // column on payload whose value keys the storage path
  payload:     Record<string, unknown>
  photo:       Blob | null
  photo_name:  string | null
  // PK column carrying a CLIENT-MINTED id. When set, the insert becomes an
  // upsert-ignore on it so a replay (lost-response live attempt then a queued
  // retry, both sharing the minted id) is a no-op instead of a duplicate row.
  conflictColumn?: string
  // Drain passes this entry has already had (entry.attempt_count). The live
  // path leaves it unset: there a held photo just means "queue it".
  photoTries?: number
}

// Upload one queued entry's photo; resolves to its storage path, throws on any
// failure (holdOrDropPhoto decides what a failure costs).
// Where a queued entry's photo goes: date / block / minted id, under the ROW's tenant.
function entryPhotoPath(input: PhotoInsertInput, mintedId: string | undefined): string | Promise<string> {
  const ext        = (input.photo_name?.split('.').pop() ?? 'jpg').toLowerCase()
  const dateKey    = (input.payload[input.dateField] as string) ?? sastTodayYMD()
  const blockId    = (input.payload.block_id as string) ?? 'no-block'
  // Key the file on the row's client-minted PK when we have one, so a
  // replay reuses the same object instead of orphaning a second upload;
  // fall back to a timestamp for legacy non-idempotent callers.
  const fileKey    = mintedId ?? String(Date.now())
  // T1.1 Phase B — tenant-prefix the path.
  //
  // ⚠️ From the ROW's tenant when it carries one, NOT the live JWT. The
  // upload happens BEFORE the insert, so a drain under a different active
  // tenant used to write farm A's orchard photo into tenant B's prefix —
  // where B's members can list and sign it — and only then get the row
  // refused. The DEFER guards in the drain make that sequence unreachable
  // for a stamped entry; this makes it unreachable full stop, including
  // for a legacy entry queued before the stamp existed. Falls back to the
  // JWT only when the payload names no tenant (the live path, where the
  // two are the same by construction).
  const rowTenant  = typeof input.payload.tenant_id === 'string' ? input.payload.tenant_id : null
  const key        = `${dateKey}/${blockId}/${fileKey}.${ext}`
  return rowTenant ? tenantPath(rowTenant, key) : tenantPathResolved(key)
}

async function uploadEntryPhoto(input: PhotoInsertInput, photo: Blob, mintedId: string | undefined): Promise<string> {
  const compressed = await compressStandard(photo as File)
  const path       = await entryPhotoPath(input, mintedId)
  // uploadWithTimeout, not a bare .upload(): guardedFetch (supabase.ts)
  // bounds /auth/v1/ and /rest/v1/ but NOT /storage/v1/, and the drain runs
  // on the SHARED client (no run* caller passes `db`), so this upload had
  // no abort at all. The drain-level withTimeout added for M2 abandons the
  // WAIT but leaves the socket open; this aborts the request itself.
  //
  // scouting-photos never overwrites (mig 20261037700000: only a file's
  // uploader or a manager may): on a minted path an object already there
  // is THIS photo from an earlier attempt, maybe uploaded under another
  // sign-in on a shared phone — use it rather than lose the photo.
  const once = input.bucket === 'scouting-photos' && !!mintedId
  const { error: upErr } = await uploadWithTimeout(
    input.bucket, path, compressed,
    { upsert: !once, contentType: photo.type || 'image/jpeg' },
    undefined, input.db ?? supabase)
  if (upErr && !(once && isAlreadyUploaded(upErr))) throw upErr
  return path
}

function holdOrDropPhoto(uploadErr: unknown, table: string, photoTries: number | undefined): void {
  // A dropped or slow link is not a reason to lose the photo. Throw a
  // network-shaped error so the live path queues the whole entry and the
  // drain keeps it (photo included) for the next pass, spending no give-up
  // budget. uploadWithTimeout's own timeout reads "taking longer than",
  // which no classifier knows, so name it here. Held for PHOTO_HOLD_PASSES
  // drain passes only: the drain stops at a transient fault, so a photo
  // that can never get through a thin link would otherwise hold every
  // later entry (pay drops included) behind it for good. After that, and
  // for anything else (a refused file, a bucket rule), the find lands and
  // only the photo is dropped.
  const m   = (uploadErr as { message?: unknown } | null)?.message
  const msg = typeof m === 'string' ? m : String(uploadErr)
  const linkFault = isTransientFault(msg) || /taking longer than/i.test(msg)
  if (linkFault && (photoTries ?? 0) < PHOTO_HOLD_PASSES) {
    throw new Error(`Photo upload network error, will retry: ${msg}`)
  }
  // eslint-disable-next-line no-console
  console.warn(`${table} photo upload failed; inserting without photo`, uploadErr)
}

async function runWithPhotoInsert(input: PhotoInsertInput): Promise<void> {
  const mintedId = input.conflictColumn ? (input.payload[input.conflictColumn] as string | undefined) : undefined
  let photoPath: string | null = (input.payload.photo_url as string | null) ?? null
  if (input.photo) {
    try {
      photoPath = await uploadEntryPhoto(input, input.photo, mintedId)
    } catch (uploadErr) {
      holdOrDropPhoto(uploadErr, input.table, input.photoTries)
    }
  }
  const row = { ...input.payload, photo_url: photoPath }
  const db  = input.db ?? supabase
  const { error } = input.conflictColumn
    ? await db.from(input.table).upsert(row, { onConflict: input.conflictColumn, ignoreDuplicates: true })
    : await db.from(input.table).insert(row)
  if (error) throw dbError(error)
}

async function runScoutingInsert(input: { payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null; extra_photos?: ScoutingExtraPhoto[]; db?: SupabaseClient; photoTries?: number }): Promise<void> {
  await runWithPhotoInsert({
    db:         input.db,
    photoTries: input.photoTries,
    table:      'scouting_observations',
    bucket:     'scouting-photos',
    dateField:  'observed_at',
    payload:    input.payload,
    photo:      input.photo,
    photo_name: input.photo_name,
    conflictColumn: 'observation_id',
  })
  await runScoutExtraPhotos(input)
}

/**
 * Photos 2..N of an observation (mig 20261017230501), uploaded and linked
 * AFTER the observation itself has landed.
 *
 * Two postures worth stating, both inherited from the single-photo path:
 *
 * 1. An extra photo NEVER costs the observation. runWithPhotoInsert already
 *    inserts photo-less rather than losing a scout's find when an upload
 *    fails; the same holds here per photo, so a bad image or a dropped
 *    connection mid-way leaves the finding and every photo that did upload.
 * 2. Both the upload and the row are keyed on the client-minted photo_id, so
 *    a replay meets its own object and no-ops its own row instead of
 *    duplicating either.
 */
/**
 * One extra photo: compress, upload under the observation's key, and shape its
 * row. Returns null when the upload fails — the caller drops it and keeps the
 * observation and every photo that did land, which is the posture
 * runWithPhotoInsert already takes for the cover.
 */
async function uploadScoutExtraPhoto(
  extra: ScoutingExtraPhoto,
  ctx: {
    db: SupabaseClient; dateKey: string; blockId: string; observationId: string
    sortOrder: number; tenantId: string | null; uploadedBy: string | null
  },
): Promise<Record<string, unknown> | null> {
  try {
    const compressed = await compressStandard(extra.blob as File)
    const ext  = (extra.photo_name?.split('.').pop() ?? 'jpg').toLowerCase()
    // Keyed on the client-minted photo_id, so a replay meets its own object
    // (isAlreadyUploaded, below) rather than orphaning a second copy.
    //
    // ⚠️ Prefixed with the ROW's tenant, not the live JWT's — the row below is
    // stamped `tenant_id: ctx.tenantId` (the origin), so resolving the path
    // from the session guaranteed the file and its own row disagreed whenever
    // the two differed: the photo lands where the owning tenant cannot read it.
    const key  = `${ctx.dateKey}/${ctx.blockId}/${extra.photo_id}.${ext}`
    const path = ctx.tenantId ? tenantPath(ctx.tenantId, key) : await tenantPathResolved(key)
    // Never overwrites (mig 20261037700000): an object already at this minted
    // path is this photo from an earlier attempt — use it (isAlreadyUploaded).
    const { error } = await uploadWithTimeout(
      'scouting-photos', path, compressed,
      { upsert: false, contentType: extra.blob.type || 'image/jpeg' }, undefined, ctx.db)
    if (error && !isAlreadyUploaded(error)) throw error
    return {
      photo_id:       extra.photo_id,
      observation_id: ctx.observationId,
      storage_path:   path,
      caption:        extra.caption,
      sort_order:     ctx.sortOrder,
      tenant_id:      ctx.tenantId,
      uploaded_by:    ctx.uploadedBy,
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[queue] a scouting extra photo failed to upload; the observation and its other photos are unaffected', e)
    return null
  }
}

async function runScoutExtraPhotos(input: {
  payload: Record<string, unknown>
  extra_photos?: ScoutingExtraPhoto[]
  db?: SupabaseClient
}): Promise<void> {
  // `?? []` is load-bearing: entries queued by a pre-multi-photo build have no
  // such field, and they drain through here on the first run after an update.
  const extras = input.extra_photos ?? []
  if (extras.length === 0) return

  const db = input.db ?? supabase
  const observationId = input.payload.observation_id as string | undefined
  if (!observationId) return

  const dateKey = (input.payload.observed_at as string) ?? sastTodayYMD()
  const blockId = (input.payload.block_id as string) ?? 'no-block'

  const settled = await Promise.all(extras.map((extra, i) =>
    uploadScoutExtraPhoto(extra, {
      db, dateKey, blockId, observationId,
      sortOrder:  i + 1,                       // 0 is the cover
      tenantId:   (input.payload.tenant_id as string | null) ?? null,
      uploadedBy: (input.payload.logged_by as string | null) ?? null,
    })))
  const rows = settled.filter((r): r is Record<string, unknown> => r !== null)
  if (rows.length === 0) return
  await insertScoutExtraPhotoRows(db, rows)
}

/**
 * The photo rows for photos 2..N, upsert-ignore on their minted photo_id. A
 * REFUSAL is kept for the person to see (ScoutPhotosRefused, above): the photos
 * are not lost, and Retry heals it under the right sign-in. Any other fault
 * keeps the old posture — the finding is already saved and is what matters.
 */
async function insertScoutExtraPhotoRows(db: SupabaseClient, rows: Record<string, unknown>[]): Promise<void> {
  const { error } = await db.from('scouting_observation_photos')
    .upsert(rows, { onConflict: 'photo_id', ignoreDuplicates: true })
  if (!error) return
  if (error.code === '42501' || isRlsDenial(error.message ?? '')) throw new ScoutPhotosRefused()
  // eslint-disable-next-line no-console
  console.warn('[queue] scouting extra photo rows failed to insert', error)
}

// Shared piece-work tally fan-out: upload each drop's optional evidence blobs
// (tally photo + mig-709 face crop), then UPSERT all rows through
// save_pick_tallies (mig 712 device-validating RPC; idempotent on
// pick_tally_id). Used by BOTH the parent-pick insert and the append-only drop
// queue (the throughout-the-day capture screen).
// fallow-ignore-next-line complexity
async function runTallyUpsert(
  pickId: string,
  tallies: Record<string, unknown>[],
  deviceToken: string | null,
  pickDate: string,
  blockId: string,
  db: SupabaseClient,
): Promise<void> {
  if (!tallies.length) return
  const rows: Record<string, unknown>[] = []
  for (const t of tallies) {
    const { _photoBlob, _photoName, _faceBlob, ...tallyRow } = t as Record<string, unknown> & {
      _photoBlob?: Blob | null; _photoName?: string | null; _faceBlob?: Blob | null
    }
    const tallyId = String(tallyRow.pick_tally_id)
    if (_photoBlob && !tallyRow.photo_url) {
      const ext = ((_photoName as string | null)?.split('.').pop() ?? 'jpg').toLowerCase()
      const stored = await uploadHarvestEvidence({
        blob: _photoBlob as Blob, compress: true, db,
        path: await tenantPathResolved(`${pickDate}/${blockId}/tally-${tallyId}.${ext}`),
        what: 'harvest tally photo upload failed; row kept',
        context: { pick_tally_id: tallyId },
      })
      if (stored) tallyRow.photo_url = stored
    }
    if (_faceBlob && !tallyRow.match_evidence_url) {
      const face = await uploadHarvestEvidence({
        blob: _faceBlob as Blob, compress: false, db,
        path: await tenantPathResolved(`${pickDate}/${blockId}/face-${tallyId}.jpg`),
        what: 'harvest face-evidence upload failed; row kept',
        context: { pick_tally_id: tallyId },
      })
      if (face) tallyRow.match_evidence_url = face
    }
    rows.push({ ...tallyRow, pick_id: pickId })
  }
  const { error } = await db.rpc('save_pick_tallies', {
    p_pick_id:      pickId,
    p_tallies:      rows,
    p_device_token: deviceToken ?? null,
  })
  if (error) throw dbError(error)
}

async function runPickTallies(input: { pick_id: string; tallies: Record<string, unknown>[]; device_token: string | null; pick_date: string; block_id: string; db?: SupabaseClient }): Promise<void> {
  await runTallyUpsert(input.pick_id, input.tallies, input.device_token, input.pick_date, input.block_id, input.db ?? supabase)
}

// fallow-ignore-next-line complexity
async function runPickInsert(input: { payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null; db?: SupabaseClient; photoTries?: number }): Promise<void> {
  // Per-picker piece-work tallies (mig 705) ride on the queued payload under
  // `_tallies` (same trick spray uses for `_recipe`). Strip them before the
  // parent insert (harvest_picks has no such column) and fan them into
  // harvest_pick_tallies after the parent row lands. Each tally carries its
  // own minted pick_tally_id, so a lost-response retry upsert-ignores instead
  // of duplicating — the same idempotency guarantee the parent pick_id gives.
  const { _tallies, _device_token, ...pickPayload } = input.payload as Record<string, unknown> & {
    _tallies?: Record<string, unknown>[]; _device_token?: string | null
  }
  await runWithPhotoInsert({
    db:         input.db,
    photoTries: input.photoTries,
    table:      'harvest_picks',
    bucket:     'harvest-pick-photos',
    dateField:  'pick_date',
    payload:    pickPayload,
    photo:      input.photo,
    photo_name: input.photo_name,
    conflictColumn: 'pick_id',
  })
  // Mig 20261016222317 degrades a no-longer-matching planting to NULL
  // server-side; picks are raw inserts with no RPC return channel, so read
  // it back (live saves AND queue drains land here) — the Phase B contract:
  // a compliance record's scope never changes silently. Best-effort: the
  // pick itself already saved.
  if (pickPayload.project_id != null && pickPayload.pick_id) {
    try {
      const { data: saved, error: readErr } = await (input.db ?? supabase).from('harvest_picks')
        .select('project_id').eq('pick_id', pickPayload.pick_id as string).is('deleted_at', null).maybeSingle()
      if (readErr) {
        // eslint-disable-next-line no-console
        console.warn('[harvest-picks] planting degrade check unavailable', readErr)
      } else if (saved && saved.project_id == null) {
        // eslint-disable-next-line no-console
        console.warn('[harvest-picks] planting no longer matches its block — recorded whole-block', pickPayload.pick_id)
        void alertDialog({ message: plantingDegradedMessage('harvest pick') })
      }
    } catch { /* readback is advisory */ }
  }
  const tallies = (_tallies ?? []) as Record<string, unknown>[]
  if (tallies.length) {
    const db       = input.db ?? supabase
    const pickDate = (pickPayload.pick_date as string) ?? sastTodayYMD()
    const blockId  = (pickPayload.block_id as string) ?? 'no-block'
    await runTallyUpsert(String(pickPayload.pick_id), tallies, _device_token ?? null, pickDate, blockId, db)
  }
}

async function runSprayInsert(input: {
  payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null; db?: SupabaseClient,
// Mig 20261016222316 — the RPC reports when it degraded a no-longer-matching
// planting to whole-block. Surfaced as a dialog when live, a console line on
// drain replay (spec Phase B contract: never discarded silently).
}): Promise<{ plantingDegraded: boolean }> {
  const db = input.db ?? supabase
  // Recipe lines (mig 294) + extra block targets (mig 281) ride on the
  // queued payload under `_recipe` / `_block_targets`. Strip both before
  // insert (spray_records doesn't know about those columns) and fan
  // them into their respective junctions after the parent row lands.
  // The mig-294 trigger writes USE rows into chemical_movements so
  // balances reflect the spray automatically.
  const { _recipe, _block_targets, ...sprayPayload } = input.payload as Record<string, unknown> & {
    _recipe?:         SprayRecipeLine[]
    _block_targets?:  SprayBlockTarget[]
  }
  const recipe        = (_recipe ?? []) as SprayRecipeLine[]
  const blockTargets  = (_block_targets ?? []) as SprayBlockTarget[]

  let photoPath: string | null = (sprayPayload.photo_url as string | null) ?? null
  if (input.photo) {
    try {
      const compressed = await compressStandard(input.photo as File)
      const ext        = (input.photo_name?.split('.').pop() ?? 'jpg').toLowerCase()
      const dateKey    = (sprayPayload.applied_at as string) ?? sastTodayYMD()
      const blockId    = (sprayPayload.block_id as string) ?? 'no-block'
      const path       = await tenantPathResolved(`${dateKey}/${blockId}/${Date.now()}.${ext}`)
      const { error: upErr } = await storageFrom('spray-evidence', db)
        .upload(path, compressed, { upsert: true, contentType: input.photo.type || 'image/jpeg' })
      if (upErr) throw upErr
      photoPath = path
    } catch (uploadErr) {
      // eslint-disable-next-line no-console
      console.warn('spray photo upload failed; inserting without photo', uploadErr)
    }
  }

  // One atomic round-trip (mig 606): parent spray_records row + recipe
  // lines + block targets. Replaces three sequential inserts — the
  // dominant latency on a slow field connection — and makes the save
  // all-or-nothing, so we no longer get a spray_records row that saved
  // while its recipe lines silently failed.
  const { data, error } = await db.rpc('save_spray_record', {
    p_payload:   { ...sprayPayload, photo_url: photoPath },
    p_recipe:    recipe,
    p_block_ids: blockTargets,
  })
  if (error) throw dbError(error)
  return { plantingDegraded: (data as { planting_degraded?: boolean } | null)?.planting_degraded === true }
}

// ---------------------------------------------------------------------------
// Pick + Spray — same shape as Scouting (plain insert + optional photo).
// ---------------------------------------------------------------------------
export async function enqueuePick(input: { payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null; tenant_id?: string | null }): Promise<void> {
  // Stamp the origin tenant so the drain can DEFER (not strand) if the active
  // tenant has since changed (mirrors enqueuePickTallies). Prefer the caller's
  // value (the modal's activeTenant — same source the payload's tenant_id is
  // stamped from); fall back to the JWT's active_tenant_id claim, which is what
  // the harvest_picks tenant_gate's current_tenant_id() resolves against.
  const tenantId = input.tenant_id ?? decodeStoredClaims()?.active_tenant_id ?? null
  const row: PickEntry = {
    id:           newUuid(),
    kind:         'pick',
    payload:      input.payload,
    photo:        input.photo,
    photo_name:   input.photo_name,
    tenant_id:    tenantId,
    attempted_at: new Date().toISOString(),
    attempt_count: 0,
    last_error:   null,
    failed:       false,
    queued_at:    new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

export async function insertOrQueuePick(input: { payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null; tenant_id?: string | null } & { db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  // Mint pick_id up front so a lost-response live insert + a queued retry
  // dedupe via upsert-ignore (runPickInsert) instead of duplicating.
  if (!input.payload.pick_id) input.payload.pick_id = newUuid()
  if (offline() || !(await supabaseReachable())) {
    await enqueuePick(input)
    return 'queued'
  }
  try {
    await withTimeout(runPickInsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'pick insert')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueuePick(input)
      return 'queued'
    }
    throw e
  }
}

async function enqueuePickTallies(input: { pick_id: string; tallies: Record<string, unknown>[]; device_token: string | null; pick_date: string; block_id: string; tenant_id?: string | null }): Promise<void> {
  // Stamp the origin tenant so the drain can defer (not strand) if the active
  // tenant has since changed. Prefer the caller's value (the modal's activeTenant
  // — same source PickEntry stamps from); fall back to the JWT's active_tenant_id
  // claim, which is exactly what save_pick_tallies' current_tenant_id() resolves.
  const tenantId = input.tenant_id ?? decodeStoredClaims()?.active_tenant_id ?? null
  const row: PickTalliesEntry = {
    id:            newUuid(),
    kind:          'pick_tallies',
    pick_id:       input.pick_id,
    tallies:       input.tallies,
    device_token:  input.device_token,
    pick_date:     input.pick_date,
    block_id:      input.block_id,
    tenant_id:     tenantId,
    attempted_at:  new Date().toISOString(),
    attempt_count: 0,
    last_error:    null,
    failed:        false,
    queued_at:     new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

// Append piece-work drops to an EXISTING pick (the throughout-the-day capture
// screen). Each drop is its own tally row; save_pick_tallies upserts by
// pick_tally_id so a lost-response retry is a no-op, not a duplicate.
//
// `forceQueue` — the pick itself is STILL SITTING IN THE OFFLINE QUEUE (not yet
// drained into harvest_picks): "Add piece-work drops" is reachable on a pending
// pick card the moment it's queued (W3 §4.1), often while the device is fully
// online. A live attempt here hits save_pick_tallies' very first check —
// `harvest_picks WHERE pick_id = … AND tenant_id = v_tenant` — before the pick
// row exists at all, which ALWAYS raises "Pick not found in current tenant"
// (misread as a tenant-switch problem; it isn't one — issue #1268). Forcing the
// tally onto the queue lets the FIFO drain land the parent pick first.
export async function insertOrQueuePickTallies(input: { pick_id: string; tallies: Record<string, unknown>[]; device_token: string | null; pick_date: string; block_id: string; tenant_id?: string | null; forceQueue?: boolean } & { db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  if (input.forceQueue || offline() || !(await supabaseReachable())) {
    await enqueuePickTallies(input)
    return 'queued'
  }
  try {
    await withTimeout(runPickTallies(input), LIVE_ATTEMPT_TIMEOUT_MS, 'pick tallies')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueuePickTallies(input)
      return 'queued'
    }
    throw e
  }
}

// ---------------------------------------------------------------------------
// Packhouse piece-work capture (mig 20261021000000) — the packhouse twin of the
// pick-tallies rail above. One RPC, one row per scan, idempotent on a
// client-minted packout_drop_id, and the server derives the day's
// packout_tallies row from the live drops.
//
// No evidence upload leg, unlike runTallyUpsert: v1 does not persist the
// aligned face crop for a packhouse credit. The farm writes its crop to
// harvest-pick-photos, which has an authenticated INSERT policy; the packhouse
// has no equivalent bucket and the face-audit one (mig 620) is service-role
// upload only. Every face ATTEMPT is still recorded in worker_face_match_log
// (source PIECEWORK), so the match rate stays measurable; the
// packout_capture_drops.match_evidence_url column is there for when a bucket
// lands.
// ---------------------------------------------------------------------------
async function runPackoutDrops(input: {
  drops: Record<string, unknown>[]; device_token: string | null; db?: SupabaseClient
}): Promise<PackoutDayTotal[]> {
  if (!input.drops.length) return []
  const { data, error } = await (input.db ?? supabase).rpc('save_packout_drops', {
    p_drops:        input.drops,
    p_device_token: input.device_token ?? null,
  })
  if (error) throw dbError(error)
  return packoutTotalsOf(data)
}

/** The five bookkeeping fields every queue entry carries.
 *
 *  Factored out when the packout rail became the ninth copy of them. The other
 *  eight are older and are deliberately left alone: rewriting eight live
 *  offline write paths to pay off a clone is a bigger risk than the clone. */
const newEntryStamp = () => ({
  attempted_at:  new Date().toISOString(),
  attempt_count: 0,
  last_error:    null,
  failed:        false,
  queued_at:     new Date().toISOString(),
})

async function enqueuePackoutDrops(input: {
  drops: Record<string, unknown>[]; device_token: string | null; work_date: string
  tenant_id?: string | null
}): Promise<void> {
  // Origin tenant stamped at enqueue — see PackoutDropsEntry. Prefer the
  // caller's active tenant; fall back to the JWT claim, which is exactly what
  // save_packout_drops' current_tenant_id() will resolve on the drain.
  const tenantId = input.tenant_id ?? decodeStoredClaims()?.active_tenant_id ?? null
  const row: PackoutDropsEntry = {
    id:           newUuid(),
    kind:         'packout_drops',
    drops:        input.drops,
    device_token: input.device_token,
    work_date:    input.work_date,
    tenant_id:    tenantId,
    ...newEntryStamp(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

export async function insertOrQueuePackoutDrops(input: {
  drops: Record<string, unknown>[]; device_token: string | null; work_date: string
  tenant_id?: string | null
} & { db?: SupabaseClient }): Promise<PackoutSaveResult> {
  if (offline() || !(await supabaseReachable())) {
    await enqueuePackoutDrops(input)
    return { status: 'queued', totals: [] }
  }
  try {
    const totals = await withTimeout(runPackoutDrops(input), LIVE_ATTEMPT_TIMEOUT_MS, 'packout drops')
    return { status: 'sent', totals }
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueuePackoutDrops(input)
      return { status: 'queued', totals: [] }
    }
    throw e
  }
}

export async function enqueueSpray(input: { payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null; tenant_id?: string | null }): Promise<void> {
  const row: SprayEntry = {
    id:           newUuid(),
    kind:         'spray',
    payload:      input.payload,
    photo:        input.photo,
    photo_name:   input.photo_name,
    tenant_id:    input.tenant_id ?? null,
    attempted_at: new Date().toISOString(),
    attempt_count: 0,
    last_error:   null,
    failed:       false,
    queued_at:    new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

// Mig 20261016222316 — plantingDegraded: the server degraded a
// no-longer-matching planting to whole-block; the caller must tell the
// operator (spec Phase B contract). A queued save can't know yet; its
// degrade surfaces at drain time (dialog + console line).
export interface SpraySaveResult {
  status: 'sent' | 'queued'
  plantingDegraded: boolean
  /** A spray-task sign-off that arrived after the task was already recorded
   *  (from paper or another phone): complete_spray_task kept the first. */
  alreadyRecorded?: AlreadyRecorded | null
  /** Part-done sign-off: the follow-up task the server planned for the rest
   *  (payload.remaining_block_ids). Only a live ('sent') completion knows it. */
  followupTaskId?: string | null
  /** The record landed but the follow-up task could not be written. */
  followupFailed?: boolean
}

export async function insertOrQueueSpray(input: { payload: Record<string, unknown>; photo: Blob | null; photo_name: string | null; tenant_id?: string | null } & { db?: SupabaseClient }): Promise<SpraySaveResult> {
  // Mint spray_id up front (mig 685 reads it from p_payload) so the live attempt
  // and any queued retry share it — save_spray_record then skips a replay
  // instead of double-inserting + double-decrementing chemical stock.
  if (!input.payload.spray_id) input.payload.spray_id = newUuid()
  // Skip the up-to-2s supabaseReachable() HEAD probe that every other queued
  // surface pays before saving. The probe only ran when navigator reports
  // online anyway, so on a healthy connection it was pure dead time on the
  // critical path of every spray save. We instead attempt the save directly
  // (bounded by LIVE_ATTEMPT_TIMEOUT_MS) and fall back to the offline queue on
  // any network-ish failure below — the "online but server unreachable" case
  // costs one timeout instead of being pre-detected, which is the rare case.
  if (offline()) {
    await enqueueSpray(input)
    return { status: 'queued', plantingDegraded: false }
  }
  try {
    const res = await withTimeout(runSprayInsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'spray insert')
    return { status: 'sent', plantingDegraded: res.plantingDegraded }
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueSpray(input)
      return { status: 'queued', plantingDegraded: false }
    }
    throw e
  }
}

// ---------------------------------------------------------------------------
// Spray TASK completion (mig 670) — "sign & complete" from the field.
// ---------------------------------------------------------------------------
// Everything complete_spray_task needs, captured at completion time so an
// out-of-signal finish still lands. spray_id (and track_id) are client-minted
// so the whole thing is one idempotent RPC the drain can replay.

export async function enqueueSprayComplete(input: SprayCompleteData): Promise<void> {
  // Explicit field list (not a spread) so a stray `db` client can never be
  // persisted into IndexedDB — mirrors enqueueSpray.
  const row: SprayCompleteEntry = {
    id:               newUuid(),
    kind:             'spray_complete',
    task_id:          input.task_id,
    spray_id:         input.spray_id,
    payload:          input.payload,
    recipe:           input.recipe,
    block_ids:        input.block_ids,
    completed_at:     input.completed_at,
    tenant_id:        input.tenant_id,
    track_id:         input.track_id,
    track_points:     input.track_points,
    track_distance_m: input.track_distance_m,
    track_started_at: input.track_started_at,
    track_ended_at:   input.track_ended_at,
    signature:        input.signature,
    photo:            input.photo,
    photo_name:       input.photo_name,
    attempted_at:     new Date().toISOString(),
    attempt_count:    0,
    last_error:       null,
    failed:           false,
    queued_at:        new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

// A spray task already recorded elsewhere keeps its record; this hangs the
// phone's GPS track on it — only into an empty slot, at the record's own
// version, never over a track it already has or an edit made since. Best-effort:
// the completion itself is settled, so a failure reads "could not be added"
// (false), never a failed sign-off. null = the phone recorded no track.
async function attachTrackToKept(db: SupabaseClient, sprayId: string, trackId: string | undefined): Promise<boolean | null> {
  if (!trackId) return null
  try {
    const { data: cur, error: rErr } = await db.from('spray_records')
      .select('version, spray_track_id').eq('spray_id', sprayId).maybeSingle()
    const row = cur as { version: number; spray_track_id: string | null } | null
    if (rErr || !row || row.spray_track_id != null) return false
    await withTimeout(updateWithVersion('spray_records', { col: 'spray_id', value: sprayId }, row.version,
      { spray_track_id: trackId }, { db }), LIVE_ATTEMPT_TIMEOUT_MS, 'spray track attach')
    return true
  } catch {
    return false
  }
}

// Step 1 of runSprayComplete. true = the track row exists (written now or on
// an earlier replay), so the record may reference it.
async function upsertSprayTrack(db: SupabaseClient, input: SprayCompleteData): Promise<boolean> {
  if (!input.track_id || !input.track_points || input.track_points.length < 2) return false
  const { error: tErr } = await db.from('spray_tracks').upsert({
    spray_track_id: input.track_id,
    track:          input.track_points,
    point_count:    input.track_points.length,
    distance_m:     input.track_distance_m,
    started_at:     input.track_started_at,
    ended_at:       input.track_ended_at,
    logged_by:      (input.payload.logged_by as string | null) ?? null,
    tenant_id:      input.tenant_id,
  }, { onConflict: 'spray_track_id', ignoreDuplicates: true })
  if (tErr) throw dbError(tErr)
  return true
}

// Step 2b of runSprayComplete. null = the upload failed; the completion still
// lands without the photo (see the call site).
async function uploadSprayCompletePhoto(db: SupabaseClient, input: SprayCompleteData, photo: Blob, stamp: number): Promise<string | null> {
  try {
    const compressed = await compressStandard(photo as File)
    const ext        = (input.photo_name?.split('.').pop() ?? 'jpg').toLowerCase()
    const dateKey    = (input.payload.applied_at as string) ?? sastTodayYMD()
    const blockId    = (input.payload.block_id as string) ?? 'no-block'
    const path       = await tenantPathResolved(`${dateKey}/${blockId}/${stamp}.${ext}`)
    const { error: upErr } = await storageFrom('spray-evidence', db)
      .upload(path, compressed, { upsert: true, contentType: photo.type || 'image/jpeg' })
    if (upErr) throw upErr
    return path
  } catch (uploadErr) {
    // eslint-disable-next-line no-console
    console.warn('spray-complete photo upload failed; completing without photo', uploadErr)
    return null
  }
}

// Step 2a of runSprayComplete: the My-Tasks signature, on a fixed task path.
async function uploadSprayCompleteSignature(db: SupabaseClient, taskId: string, signature: Blob, stamp: number): Promise<string> {
  const path = await tenantPathResolved(`${taskId}/signature-${stamp}.png`)
  const { error: upErr } = await storageFrom('spray-evidence', db)
    .upload(path, signature, { upsert: true, contentType: 'image/png' })
  if (upErr) throw dbError(upErr)
  return path
}

// Step 4 of runSprayComplete: null unless the RPC kept an earlier record.
async function keptRecordWithTrack(db: SupabaseClient, data: unknown, sprayId: string, trackId: string | undefined): Promise<AlreadyRecorded | null> {
  const kept = alreadyRecordedFrom(data, sprayId)
  return kept ? { sprayId: kept.sprayId, trackAttached: await attachTrackToKept(db, kept.sprayId, trackId) } : null
}

// What complete_spray_task reports beyond the record itself.
// Mig 20261016222316: it degraded a no-longer-matching planting to whole-block;
// never discard the signal (spec Phase B contract). Mig 20261207000000: the
// follow-up task a part-done sign-off planned for the rest, or that it failed.
function sprayCompleteFlags(data: unknown): { plantingDegraded: boolean; followupTaskId: string | null; followupFailed: boolean } {
  const d = (data ?? {}) as { planting_degraded?: boolean; followup_task_id?: string | null; followup_failed?: boolean }
  return {
    plantingDegraded: d.planting_degraded === true,
    followupTaskId:   d.followup_task_id ?? null,
    followupFailed:   d.followup_failed === true,
  }
}

async function runSprayComplete(input: SprayCompleteData & { db?: SupabaseClient }): Promise<{ plantingDegraded: boolean; alreadyRecorded: AlreadyRecorded | null; followupTaskId: string | null; followupFailed: boolean }> {
  const db = input.db ?? supabase
  const payload: Record<string, unknown> = { ...input.payload }
  // Stable stamp (the completion instant, not drain time) so a replay reuses
  // the same storage path instead of orphaning a second evidence object.
  const stamp = Number.isFinite(Date.parse(input.completed_at)) ? Date.parse(input.completed_at) : Date.now()

  // 1. GPS track (if captured) — client-minted id, upsert+ignoreDuplicates so a
  // replay is a no-op. tenant_id embedded (don't rely on the column default at
  // drain). On success the spray record references it.
  if (await upsertSprayTrack(db, input)) payload.spray_track_id = input.track_id

  // 2a. Signature (My-Tasks flow) — fixed task path.
  if (input.signature) payload.applicator_signature_url = await uploadSprayCompleteSignature(db, input.task_id, input.signature, stamp)

  // 2b. Photo (full Records form) — date/block path, compressed like the
  // free-form spray-record queue path. A failed upload doesn't block the
  // completion (we can't re-prompt the operator hours later) — the record
  // still lands, just without the photo.
  if (input.photo) {
    const photoPath = await uploadSprayCompletePhoto(db, input, input.photo, stamp)
    if (photoPath) payload.photo_url = photoPath
  }

  // 3. Idempotent completion (mig 670): inserts the record + recipe + block
  // targets and flips the task in one round-trip, skipping re-inserts on replay
  // (no double stock decrement) and never double-completing a task.
  const { data, error } = await db.rpc('complete_spray_task', {
    p_task_id:      input.task_id,
    p_spray_id:     input.spray_id,
    p_payload:      payload,
    p_recipe:       input.recipe,
    p_block_ids:    input.block_ids,
    p_completed_at: input.completed_at,
  })
  if (error) throw dbError(error)

  // 4. The task was already recorded (sprayAlreadyRecorded.ts): the RPC kept
  // that record and ignored this payload, so the track written in step 1 would
  // hang on nothing. Attach it to the kept record (attachTrackToKept).
  const alreadyRecorded = await keptRecordWithTrack(db, data, input.spray_id, payload.spray_track_id as string | undefined)

  return { ...sprayCompleteFlags(data), alreadyRecorded }
}

export async function insertOrQueueSprayComplete(input: SprayCompleteData & { db?: SupabaseClient }): Promise<SpraySaveResult> {
  // Mirror the spray-record path: skip the reachability probe, attempt live
  // (bounded), fall back to the queue on any network-ish failure.
  if (offline()) {
    await enqueueSprayComplete(input)
    return { status: 'queued', plantingDegraded: false }
  }
  try {
    const res = await withTimeout(runSprayComplete(input), LIVE_ATTEMPT_TIMEOUT_MS, 'spray complete')
    return { status: 'sent', plantingDegraded: res.plantingDegraded, alreadyRecorded: res.alreadyRecorded, followupTaskId: res.followupTaskId, followupFailed: res.followupFailed }
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueSprayComplete(input)
      return { status: 'queued', plantingDegraded: false }
    }
    if (isRlsDenial(msg)) {
      throw new ValidationError(
        "You don't have permission to complete spray tasks. Pull to refresh; "
        + "if it keeps failing, ask a farm admin to check your role.",
      )
    }
    throw e
  }
}

// ---- Logbook trips + tractor field operations (FM08 UF-26) -----------------
// Both are plain single-table inserts with NO photo (so there is no blob to
// hold), logged in the bakkie or the field where there is often no signal.
//   * idempotent: the PK (trip_id / tractor_operation_id) is minted up front and
//     the insert is upsert-ignore, so a lost-response live attempt + a queued
//     retry write ONE row;
//   * tenant-stamped at ENQUEUE, on the payload AND the entry: the column
//     DEFAULT current_tenant_id() resolves at DRAIN, possibly after a tenant
//     switch, and the RLS check compares it to the JWT. The drain DEFERS (keeps,
//     never fails) an entry whose tenant is not the active one;
//   * EDITS stay online-only (updateWithVersion needs the version), as for a
//     service event.
// distance_km on a trip is GENERATED by the database; it is never queued.
type FarmCaptureTable = 'vehicle_trips' | 'tractor_operations'
const FARM_CAPTURE_PK: Record<FarmCaptureTable, string> = {
  vehicle_trips: 'trip_id', tractor_operations: 'tractor_operation_id',
}

async function runFarmCaptureInsert(table: FarmCaptureTable, payload: Record<string, unknown>, db: SupabaseClient = supabase): Promise<void> {
  const { error } = await db.from(table)
    .upsert(payload, { onConflict: FARM_CAPTURE_PK[table], ignoreDuplicates: true })
  if (error) throw dbError(error)
}

async function enqueueFarmCapture(kind: 'vehicle_trip' | 'field_operation', payload: Record<string, unknown>): Promise<void> {
  const when = new Date().toISOString()
  const row: VehicleTripEntry | FieldOperationEntry = {
    kind, payload, tenant_id: originTenantOf(payload),
    ...{ id: newUuid(), attempted_at: when, queued_at: when, attempt_count: 0, last_error: null, failed: false },
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

async function insertOrQueueFarmCapture(
  kind: 'vehicle_trip' | 'field_operation', table: FarmCaptureTable,
  input: { payload: Record<string, unknown>; db?: SupabaseClient }, noun: string,
): Promise<'sent' | 'queued'> {
  const pk = FARM_CAPTURE_PK[table]
  if (!input.payload[pk]) input.payload[pk] = newUuid()
  // Same hot-path posture as fuel: no reachability probe; attempt directly,
  // bounded, and fall back to the queue on any network-ish failure.
  if (offline()) {
    await enqueueFarmCapture(kind, input.payload)
    return 'queued'
  }
  try {
    await withTimeout(runFarmCaptureInsert(table, input.payload, input.db), LIVE_ATTEMPT_TIMEOUT_MS, `${noun} insert`)
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueFarmCapture(kind, input.payload)
      return 'queued'
    }
    if (isRlsDenial(msg)) {
      throw new ValidationError(
        `You don't have permission to log ${noun}s. Pull to refresh; if it keeps failing, ask a farm admin to check your role.`,
      )
    }
    throw e
  }
}

export const insertOrQueueVehicleTrip = (input: { payload: Record<string, unknown>; db?: SupabaseClient }) =>
  insertOrQueueFarmCapture('vehicle_trip', 'vehicle_trips', input, 'trip')

export const insertOrQueueFieldOperation = (input: { payload: Record<string, unknown>; db?: SupabaseClient }) =>
  insertOrQueueFarmCapture('field_operation', 'tractor_operations', input, 'field operation')

// ---- Fuel rows (mig 603) — issues, deliveries, dips, and new-tank creates ---
type FuelTable = 'fuel_movements' | 'fuel_tanks'

export async function enqueueFuel(payload: Record<string, unknown>, table?: FuelTable): Promise<void> {
  const row: FuelEntry = {
    id:            newUuid(),
    kind:          'fuel',
    table,
    payload,
    attempted_at:  new Date().toISOString(),
    attempt_count: 0,
    last_error:    null,
    failed:        false,
    queued_at:     new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

// PK column per fuel table — the conflict target for idempotent replay.
function fuelPk(table?: FuelTable): string {
  return (table ?? 'fuel_movements') === 'fuel_tanks' ? 'tank_id' : 'movement_id'
}

async function runFuelInsert(input: { payload: Record<string, unknown>; table?: FuelTable; db?: SupabaseClient }): Promise<void> {
  const db = input.db ?? supabase
  // Upsert-ignore on the client-minted PK so a lost-response live attempt + a
  // queued retry dedupe instead of double-posting a fuel movement (which would
  // double-move the tank balance via the mig-603 trigger).
  const { error } = await db.from(input.table ?? 'fuel_movements')
    .upsert(input.payload, { onConflict: fuelPk(input.table), ignoreDuplicates: true })
  if (error) throw dbError(error)
}

// Field hot-path: a fuel issue / delivery / dip / new tank logged at the bowser
// may have no signal. Queue it and drain on reconnect — the tank balance, the
// dip delta, the equipment-counter bump and the low-fuel alert all resolve
// server-side when the row lands.
export async function insertOrQueueFuel(input: { payload: Record<string, unknown>; table?: FuelTable } & { db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  // Mint the PK up front (movement_id / tank_id by table) so the live attempt
  // and any queued retry share it → upsert-ignore dedupes a lost-response replay.
  const pkCol = fuelPk(input.table)
  if (!input.payload[pkCol]) input.payload[pkCol] = newUuid()
  // Skip the up-to-2s supabaseReachable() probe on this field hot-path (same as
  // spray): attempt directly, bounded by LIVE_ATTEMPT_TIMEOUT_MS, and fall back
  // to the queue on any network-ish failure below.
  if (offline()) {
    await enqueueFuel(input.payload, input.table)
    return 'queued'
  }
  try {
    await withTimeout(runFuelInsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'fuel insert')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueFuel(input.payload, input.table)
      return 'queued'
    }
    throw e
  }
}

// ---------------------------------------------------------------------------
// Audit must-fix #6 — labour writes that benefit from queue support.
// manager_attest_clock + adjust_labour_log_times: a supervisor on the
// floor losing Wi-Fi can complete the modal + have it sync when the
// connection returns.
//
// IDEMPOTENCY caveat: adjust_labour_log_times is idempotent (UPDATE by
// log_id). manager_attest_clock is NOT — server-side has only a ±30s
// cooldown lock, so a queued attest drained hours later could create a
// duplicate clock_scans row. The modal's queued-toast surfaces this
// (operator should not re-submit while offline). Future hardening:
// client-minted UUID + partial UNIQUE on clock_scans for attestations
// (the same shape the offline face-clock queue uses).
// ---------------------------------------------------------------------------
// ---- BigHarvest span clock-ins (D3) — record_span_clock_in RPC -------------
// Idempotent server-side on each scan's client-minted client_scan_id, so a
// lost-response live attempt + a queued retry insert exactly once.
type SpanClockInput = Omit<SpanClockEntry, 'id' | 'kind' | 'attempted_at' | 'attempt_count' | 'last_error' | 'queued_at' | 'failed'>

async function enqueueSpanClock(input: SpanClockInput): Promise<void> {
  const row: SpanClockEntry = {
    id:            newUuid(),
    kind:          'span_clock',
    ...input,
    attempted_at:  new Date().toISOString(),
    attempt_count: 0,
    last_error:    null,
    failed:        false,
    queued_at:     new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

/** P45-08: ONE upload path for every piece of harvest evidence (DN photo,
 *  tally photo, face match). Three hand-copied try/catch blocks had already
 *  drifted — one compressed, one didn't — and each duplicated the timeout +
 *  report + warn shape. Returns the stored path, or null when it failed.
 *
 *  NEVER throws: evidence is corroboration and the row it belongs to (a load, a
 *  tally) is money-bearing and must land regardless. The loss is reported with
 *  force:true because the dominant field failure — a dropped socket — reports
 *  "Load failed" / "Failed to fetch", both in SWALLOWED_PATTERNS, so an
 *  unforced report is inert in exactly the case it exists for. */
async function uploadHarvestEvidence(input: {
  blob:     Blob
  path:     string
  compress: boolean
  what:     string
  context:  Record<string, unknown>
  db?:      typeof supabase
}): Promise<string | null> {
  try {
    const body = input.compress ? await compressStandard(input.blob as File) : input.blob
    // uploadWithTimeout, not a bare .upload(): guardedFetch (supabase.ts)
    // bounds /auth/v1/ and /rest/v1/ but NOT /storage/v1/, so a bare upload
    // had no abort at all and one hung socket stalled the drain behind it.
    const { error } = await uploadWithTimeout(
      'harvest-pick-photos', input.path, body,
      { upsert: true, contentType: input.blob.type || 'image/jpeg' },
      undefined, input.db ?? supabase)
    if (error) throw error
    return input.path
  } catch (e) {
    void reportError(e, {
      source: 'manual', level: 'warning', force: true,
      context: { what: input.what, ...input.context },
    })
    // eslint-disable-next-line no-console
    console.warn(input.what, e)
    return null
  }
}

/** 'sent' = the RPC landed rows · 'queued' = offline/transient, it will drain ·
 *  'noop' = the RPC SUCCEEDED and inserted NOTHING (P46-04). record_span_clock_in
 *  skips a stray OUT silently and returns its inserted count, which this
 *  function used to discard — so a batch that wrote nothing was indistinguishable
 *  from one that wrote everything, and the caller cheerfully reported success.
 *  'noop' is an ADDITIVE third member: existing `=== 'queued'` checks are
 *  unaffected. A PARTIAL insert still reads 'sent' — the guard skipping some
 *  scans (already-closed workers) is it working as designed; only "nothing at
 *  all landed" is a fact the user must be told. */
export async function insertOrQueueSpanClock(input: SpanClockInput): Promise<'sent' | 'queued' | 'noop'> {
  if (offline()) {
    await enqueueSpanClock(input)
    return 'queued'
  }
  try {
    const { data, error } = await withTimeout(
      supabase.rpc('record_span_clock_in', { p_scans: input.scans }),
      LIVE_ATTEMPT_TIMEOUT_MS,
      'record_span_clock_in',
    )
    if (error) {
      if (isTransientFault(error.message)) {
        await enqueueSpanClock(input)
        return 'queued'
      }
      throw dbError(error)
    }
    return spanClockOutcome(data)
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueSpanClock(input)
      return 'queued'
    }
    throw e
  }
}

// ---- BigHarvest loads (P4.5) — record_harvest_load RPC ---------------------
// The trailer usually leaves from a block with no signal, so this is an
// offline-first path by default. Idempotent server-side on the client-minted
// load_id, so a lost-response live attempt + a queued retry insert exactly
// once; the same RPC upserts the delivery note, so two supervisors draining
// the same DN number attach to one note instead of duplicating it.
type HarvestLoadInput = Omit<HarvestLoadEntry, 'id' | 'kind' | 'attempted_at' | 'attempt_count' | 'last_error' | 'queued_at' | 'failed'>

async function enqueueHarvestLoad(input: HarvestLoadInput): Promise<void> {
  const row: HarvestLoadEntry = {
    id:            newUuid(),
    kind:          'harvest_load',
    ...input,
    attempted_at:  new Date().toISOString(),
    attempt_count: 0,
    last_error:    null,
    failed:        false,
    queued_at:     new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

export async function insertOrQueueHarvestLoad(input: HarvestLoadInput): Promise<'sent' | 'queued'> {
  // A DN photo can only be uploaded with a live connection; queue whenever one
  // is pending so the blob rides to the drain rather than being dropped.
  if (offline() || input._photoBlob) {
    await enqueueHarvestLoad(input)
    return 'queued'
  }
  try {
    const { error } = await withTimeout(
      supabase.rpc('record_harvest_load', { p_load: input.load }),
      LIVE_ATTEMPT_TIMEOUT_MS,
      'record_harvest_load',
    )
    if (error) {
      if (isTransientFault(error.message)) {
        await enqueueHarvestLoad(input)
        return 'queued'
      }
      throw dbError(error)
    }
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueHarvestLoad(input)
      return 'queued'
    }
    throw e
  }
}

type LabourAttestInput = Omit<LabourAttestEntry, 'id' | 'kind' | 'attempted_at' | 'attempt_count' | 'last_error' | 'queued_at' | 'failed'>

export async function enqueueLabourAttest(input: LabourAttestInput): Promise<void> {
  const row: LabourAttestEntry = {
    id:            newUuid(),
    kind:          'labour_attest',
    ...input,
    // Stamped here rather than passed by the caller: every attest surface wants
    // the tenant that was active at capture, and none of them should have to
    // remember. See LabourAttestEntry.tenant_id for what goes wrong without it.
    tenant_id:     decodeStoredClaims()?.active_tenant_id ?? null,
    attempted_at:  new Date().toISOString(),
    attempt_count: 0,
    last_error:    null,
    failed:        false,
    queued_at:     new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

export async function insertOrQueueLabourAttest(input: LabourAttestInput): Promise<'sent' | 'queued'> {
  if (offline()) {
    await enqueueLabourAttest(input)
    return 'queued'
  }
  try {
    const { error } = await withTimeout(
      supabase.rpc('manager_attest_clock', {
        p_worker_number: input.worker_number,
        p_status_code:   input.status_code,
        p_reason:        input.reason,
        p_device_id:     input.device_id,
        p_scanned_at:    input.scanned_at,
      }),
      LIVE_ATTEMPT_TIMEOUT_MS,
      'manager_attest_clock',
    )
    if (error) {
      if (isTransientFault(error.message)) {
        await enqueueLabourAttest(input)
        return 'queued'
      }
      throw dbError(error)
    }
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueLabourAttest(input)
      return 'queued'
    }
    throw e
  }
}

type LabourAdjustInput = Omit<LabourAdjustEntry, 'id' | 'kind' | 'attempted_at' | 'attempt_count' | 'last_error' | 'queued_at' | 'failed'>

export async function enqueueLabourAdjust(input: LabourAdjustInput): Promise<void> {
  const row: LabourAdjustEntry = {
    id:            newUuid(),
    kind:          'labour_adjust',
    ...input,
    attempted_at:  new Date().toISOString(),
    attempt_count: 0,
    last_error:    null,
    failed:        false,
    queued_at:     new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

export async function insertOrQueueLabourAdjust(input: LabourAdjustInput): Promise<'sent' | 'queued'> {
  if (offline()) {
    await enqueueLabourAdjust(input)
    return 'queued'
  }
  try {
    const { error } = await withTimeout(
      supabase.rpc('adjust_labour_log_times', {
        p_log_id:       input.log_id,
        p_check_in_at:  input.check_in_at,
        p_check_out_at: input.check_out_at,
        p_reason:       input.reason,
      }),
      LIVE_ATTEMPT_TIMEOUT_MS,
      'adjust_labour_log_times',
    )
    if (error) {
      if (isTransientFault(error.message)) {
        await enqueueLabourAdjust(input)
        return 'queued'
      }
      throw dbError(error)
    }
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueLabourAdjust(input)
      return 'queued'
    }
    throw e
  }
}

// ---------------------------------------------------------------------------
// Stock movements (Phase 2 wave 2e) — scan-driven RECEIVE / ISSUE / TRANSFER
// replay through the same gated RPCs the live path uses, so FEFO, lot
// minting, and the location guard all re-apply on drain. Signed deltas are
// order-safe. STOCKTAKE is deliberately unsupported here (post-time
// reconcile semantics — see StockMovementEntry in types.ts).
// ---------------------------------------------------------------------------
type StockMovementInput = Omit<StockMovementEntry, 'id' | 'kind' | 'attempted_at' | 'attempt_count' | 'last_error' | 'queued_at' | 'failed'>

// Split along the two RPCs it dispatches to: one branch each, so neither
// carries the other's parameter list. p_request_id (mig 20260811060909) is the
// client-minted idempotency key — the same id on the live attempt and every
// replay of it, which is what lets the server tell a replay from a second
// genuine write.
async function runStockTransfer(entry: StockMovementInput, db: SupabaseClient): Promise<void> {
  const { error } = await db.rpc('transfer_stock', {
    p_item_id:       entry.item_id,
    p_from_location: entry.from_location,
    p_to_location:   entry.to_location,
    p_qty:           entry.quantity,
    p_reason:        entry.reason,
    p_lot_id:        entry.lot_id ?? null,
    p_request_id:    entry.request_id ?? null,
  })
  if (error) throw dbError(error)
}

async function runStockLedgerWrite(entry: StockMovementInput, db: SupabaseClient): Promise<void> {
  const { error } = await db.rpc('record_stock_movement', {
    p_item_id:     entry.item_id,
    p_kind:        entry.op,
    p_quantity:    entry.quantity,
    p_reason:      entry.reason,
    p_reference:   entry.reference,
    p_batch_code:  entry.batch_code ?? null,
    p_expires_at:  entry.expires_at ?? null,
    p_lot_id:      entry.lot_id ?? null,
    p_location_id: entry.location_id ?? null,
    p_request_id:  entry.request_id ?? null,
    p_owner_id:    entry.owner_id ?? null,
  })
  if (error) throw dbError(error)
}

async function runStockMovement(entry: StockMovementInput, db: SupabaseClient = supabase): Promise<void> {
  return entry.op === 'TRANSFER'
    ? runStockTransfer(entry, db)
    : runStockLedgerWrite(entry, db)
}

async function enqueueStockMovement(input: StockMovementInput): Promise<void> {
  const now = new Date().toISOString()
  const stamp = { attempted_at: now, attempt_count: 0, last_error: null, failed: false, queued_at: now }
  const row: StockMovementEntry = { id: newUuid(), kind: 'stock_movement', ...input, ...stamp }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

export async function insertOrQueueStockMovement(input: StockMovementInput & { db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  const { db, ...rest } = input
  // Mint the idempotency key ONCE, before the live attempt — so the live call
  // and any queued replay of it carry the SAME id and the server can tell them
  // apart from two genuine writes. Minting it at enqueue time instead would be
  // too late: the live attempt that timed out at 8s is precisely the one that
  // may already have committed.
  const entry: StockMovementInput = { ...rest, request_id: rest.request_id ?? newUuid() }
  if (offline()) {
    await enqueueStockMovement(entry)
    return 'queued'
  }
  try {
    await withTimeout(runStockMovement(entry, db), LIVE_ATTEMPT_TIMEOUT_MS, 'stock movement')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg, (e as { code?: string | null } | null)?.code ?? null)) {
      await enqueueStockMovement(entry)
      return 'queued'
    }
    throw e
  }
}

// ---------------------------------------------------------------------------
// Stock count lines (STK-32) — the cycle-count worksheet, filled in the store.
// The one packhouse flow the manual sends an operator OFF signal to perform,
// and each counted figure saves on blur: offline that was one alert per line
// and a whole count lost on the next reload.
//
// The excluded-STOCKTAKE carve-out above does NOT apply here. That one
// reconciles against server truth at WRITE time; a count session reconciles at
// POST time under a row lock (post_stock_count_session), so a figure captured
// hours earlier still posts against the right baseline.
// ---------------------------------------------------------------------------
type StockCountLineInput = Omit<StockCountLineEntry, 'id' | 'kind' | 'attempted_at' | 'attempt_count' | 'last_error' | 'queued_at' | 'failed'>

async function runStockCountLine(entry: StockCountLineInput, db: SupabaseClient = supabase): Promise<void> {
  const { data, error } = await db
    .from('stock_count_lines')
    .update({ ...entry.patch, counted_at: entry.counted_at, counted_by: entry.counted_by })
    .eq('line_id', entry.line_id)
    .select('line_id')
  if (error) throw dbError(error)
  // A line the caller can no longer write (row gone, RLS scope changed) is a
  // ZERO-row UPDATE, which PostgREST reports as success — the entry would be
  // deleted and the operator told their count landed. Raise instead: the drain
  // retries, then strands it visibly in the badge with this sentence. Since
  // mig 20261054700000 (F12 R1) a counter holding the stock "edit" right writes
  // lines on an OPEN session only, so a session sent for review before the
  // figure landed is a third cause, and the one a counter hits; a counter who
  // does not hold that right at all (the worksheet still shows them fields
  // until UF-10) is the fourth.
  if ((data ?? []).length === 0) {
    throw new Error(`Count line ${entry.line_id.slice(0, 8)} did not update — the count was sent for review `
                  + `or removed, you are signed in to a different business, or you do not hold the stock `
                  + `"edit" right in this business (ask an admin). Re-enter the figure on the worksheet, `
                  + `or give it to the manager reviewing the count.`)
  }
}

async function enqueueStockCountLine(input: StockCountLineInput): Promise<void> {
  const now = new Date().toISOString()
  const row: StockCountLineEntry = {
    id: newUuid(), kind: 'stock_count_line', ...input,
    attempted_at: now, attempt_count: 0, last_error: null, failed: false, queued_at: now,
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

export async function updateOrQueueStockCountLine(
  input: Omit<StockCountLineInput, 'counted_at' | 'counted_by'>,
): Promise<'sent' | 'queued'> {
  // Stamp the capture instant and the counter ONCE, before the live attempt, so
  // the live write and any replay of it record the same thing. The identity
  // comes from the stored token's `sub` rather than auth.getSession(), which
  // awaits a refresh on an expired token — the normal state of a phone that has
  // been in a cold store all morning.
  const entry: StockCountLineInput = {
    ...input,
    counted_at: new Date().toISOString(),
    counted_by: decodeStoredClaims()?.sub ?? null,
  }
  if (offline()) {
    await enqueueStockCountLine(entry)
    return 'queued'
  }
  try {
    await withTimeout(runStockCountLine(entry), LIVE_ATTEMPT_TIMEOUT_MS, 'stock count line')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg, (e as { code?: string | null } | null)?.code ?? null)) {
      await enqueueStockCountLine(entry)
      return 'queued'
    }
    throw e
  }
}

// ---------------------------------------------------------------------------
// Phenology — single RPC call (upsert_phenology_event) keyed on
// (block_id, stage_id, observed_at) so replay is idempotent. Photo
// optional, lives in 'spray-evidence' bucket alongside Phenology's
// live writes.
// ---------------------------------------------------------------------------
// tenant_id is REQUIRED of the caller, not optional like the three farm kinds
// above: the phenology payload is an RPC argument list with no tenant in it,
// and upsert_phenology_event stamps current_tenant_id() at DRAIN time. A
// caller that cannot name the tenant cannot safely queue this.
type PhenologyInput = Pick<PhenologyEntry, 'payload' | 'photo' | 'photo_name'> & {
  tenant_id: string | null
  /** Live save only: the stage went in but its photo was refused for good. */
  onPhotoDropped?: () => void
}

export async function enqueuePhenology(input: PhenologyInput): Promise<void> {
  const row: PhenologyEntry = {
    id:           newUuid(),
    kind:         'phenology',
    payload:      input.payload,
    tenant_id:    input.tenant_id,
    photo:        input.photo,
    photo_name:   input.photo_name,
    attempted_at: new Date().toISOString(),
    attempt_count: 0,
    last_error:   null,
    failed:       false,
    queued_at:    new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

export async function insertOrQueuePhenology(input: PhenologyInput & { db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  if (offline() || !(await supabaseReachable())) {
    await enqueuePhenology(input)
    return 'queued'
  }
  try {
    await withTimeout(runPhenologyUpsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'phenology upsert')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueuePhenology(input)
      return 'queued'
    }
    throw e
  }
}

// A link fault on the photo inside the hold re-throws a network-shaped error, so
// the live save queues the stage WITH its photo and the drain retries both
// (holdOrDropPhoto, as the scouting arm). A refusal saves the stage without the
// photo and tells the caller through `onPhotoDropped`, so the screen can say so.
async function runPhenologyUpsert(input: PhenologyInput & { photoTries?: number }): Promise<void> {
  let photoPath: string | null = null
  if (input.photo) {
    try {
      const compressed = await compressStandard(input.photo as File)
      const ext        = (input.photo_name?.split('.').pop() ?? 'jpg').toLowerCase()
      const dateKey    = input.payload.p_observed_at
      const path       = await tenantPathResolved(`phenology/${dateKey}/${input.payload.p_block_id}/${Date.now()}.${ext}`)
      // Bounded — see the runWithPhotoInsert note above.
      const { error: upErr } = await uploadWithTimeout(
        'spray-evidence', path, compressed,
        { upsert: true, contentType: input.photo.type || 'image/jpeg' })
      if (upErr) throw upErr
      photoPath = path
    } catch (uploadErr) {
      holdOrDropPhoto(uploadErr, 'phenology', input.photoTries)
      input.onPhotoDropped?.()
    }
  }
  const { error } = await supabase.rpc('upsert_phenology_event', {
    ...input.payload,
    p_photo_url: photoPath,
  })
  if (error) throw dbError(error)
}

// ---------------------------------------------------------------------------
// Reject log — operator adds weight to an existing OPEN reject_bins row, to a
// NEW bin (F06 D2: `new_bin`, its id minted on the device so the bin and its
// first entry queue together; the server's trigger assigns the bin_number when
// it lands), or logs weight with no bin at all ("Don't bin (track weight
// only)", payload reject_bin_id = null — the column is nullable and
// reject_log_sync_bin skips a null bin). Only a payload that names a bin is
// ever QUEUED. Photo is optional; uploaded on drain like the other
// photo-bearing kinds.
// ---------------------------------------------------------------------------
type RejectInput = { payload: Record<string, unknown>; new_bin?: Record<string, unknown> | null; photo: Blob | null; photo_name: string | null }

export async function enqueueReject(input: RejectInput): Promise<void> {
  const row: RejectEntry = {
    id:           newUuid(),
    kind:         'reject',
    payload:      input.payload,
    new_bin:      input.new_bin ?? null,
    // Origin tenant, for a NEW bin only — see RejectEntry.tenant_id (the new
    // bin's tenant is the column default at drain time, so the drain must run
    // in this one).
    ...(input.new_bin ? { tenant_id: decodeStoredClaims()?.active_tenant_id ?? null } : {}),
    photo:        input.photo,
    photo_name:   input.photo_name,
    ...baseEntryMeta(),
  }
  await persistNewEntry(row)
}

async function runRejectInsert(input: RejectInput & { db?: SupabaseClient }): Promise<void> {
  const db = input.db ?? supabase
  const rejectId = input.payload.reject_id as string | undefined
  // F06 D2 — the new bin first, so the entry's reject_bin_id has a row to
  // point at. Upsert-ignore on the device-minted id: a replay after a lost
  // response (or a drop between the two writes) is a no-op, never a second
  // bin. A refused bin throws here, before any photo upload or entry.
  if (input.new_bin) {
    const { error: binErr } = await db.from('reject_bins').upsert(
      input.new_bin,
      { onConflict: 'reject_bin_id', ignoreDuplicates: true },
    )
    if (binErr) throw dbError(binErr)
  }
  let photoPath: string | null = (input.payload.photo_url as string | null) ?? null
  if (input.photo) {
    try {
      const compressed = await compressStandard(input.photo as File)
      const ext        = (input.photo_name?.split('.').pop() ?? 'jpg').toLowerCase()
      const runId      = (input.payload.run_id as string) ?? 'no-run'
      // Key on the client-minted reject_id (stable) so a replay reuses the same
      // object instead of orphaning a second upload.
      const fileKey    = rejectId ?? String(Date.now())
      const path       = await tenantPathResolved(`rejects/${runId}/${fileKey}.${ext}`)
      // Bounded — see the runWithPhotoInsert note above.
      const { error: upErr } = await uploadWithTimeout(
        'delivery-photos', path, compressed,
        { upsert: true, contentType: input.photo.type || 'image/jpeg' },
        undefined, db)
      if (upErr) throw upErr
      photoPath = path
    } catch (uploadErr) {
      // eslint-disable-next-line no-console
      console.warn('reject photo upload failed; inserting without photo', uploadErr)
    }
  }
  // Upsert-ignore on reject_id so a lost-response replay doesn't double-log a
  // reject (which would double the FILLED-event / bin-count side effects).
  const { error } = await db.from('reject_log').upsert(
    { ...input.payload, photo_url: photoPath },
    { onConflict: 'reject_id', ignoreDuplicates: true },
  )
  if (error) throw dbError(error)
}

// F06 D1 — "Don't bin (track weight only)" is never queued (approved spec:
// online it saves straight through; offline the form says plainly it cannot
// be kept on the device). The guard used to sit BEFORE the online check, so
// the option was refused on a live connection every time from 13 May.
// Both sentences avoid "connection" / "network" / "timeout": SubmitOverlay
// classifies those as transient and adds "Try again", which is wrong for both
// (a retry offline is refused again; a retry after a drop mints a fresh
// reject_id and can count the kg twice). rejectDontBin.test.ts pins this.
export const DONT_BIN_OFFLINE_MESSAGE =
  "No signal — a \"Don't bin\" entry can't be kept on this device to send later. " +
  'Save it again once the signal is back, or put it in a bin — an open bin or a new one saves offline.'
// The live attempt dropped: it may or may not have landed, and a retry mints a
// fresh reject_id, so the operator checks the list before logging it again.
export const DONT_BIN_DROPPED_MESSAGE =
  "The signal dropped while saving this \"Don't bin\" entry, and it can't be kept on this device. " +
  "Check the run's reject list before logging it again — it may have saved."

// A queued new bin whose origin business was not recorded (the session's
// claims could not be read when it was saved). Worded without "connection" /
// "network" — the badge is not a transient fault and a retry will not help.
export const NEW_BIN_UNSTAMPED_MESSAGE =
  "This reject entry and its new bin were saved without a record of which business they belong to, so they can't be sent. " +
  'Log the weight again in the right business, then remove this entry.'

// F06 D2 — a new bin gets its id HERE, on the device, so the live attempt
// and any queued retry name the same bin and the entry can point at it
// before the server has seen it. Its RB number arrives when it sends.
// Returns whether the entry names a bin (only such an entry is ever queued).
function attachNewRejectBin(input: RejectInput): boolean {
  if (input.new_bin) {
    if (!input.new_bin.reject_bin_id) input.new_bin.reject_bin_id = newUuid()
    input.payload.reject_bin_id = input.new_bin.reject_bin_id
  }
  return !!input.payload.reject_bin_id
}

export async function insertOrQueueReject(input: RejectInput & { db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  const hasBin = attachNewRejectBin(input)
  // Mint reject_id up front so the live attempt + any queued retry share it →
  // upsert-ignore (runRejectInsert) dedupes a lost-response replay.
  if (!input.payload.reject_id) input.payload.reject_id = newUuid()
  if (offline() || !(await supabaseReachable())) {
    if (!hasBin) throw new ValidationError(DONT_BIN_OFFLINE_MESSAGE)
    await enqueueReject(input)
    return 'queued'
  }
  try {
    await withTimeout(runRejectInsert(input), LIVE_ATTEMPT_TIMEOUT_MS, 'reject insert')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      if (!hasBin) throw new ValidationError(DONT_BIN_DROPPED_MESSAGE)
      await enqueueReject(input)
      return 'queued'
    }
    throw e
  }
}

// ---------------------------------------------------------------------------
// QC — multi-row queue support. A queued QC entry carries the qc_runs
// payload + an array of qc_responses. The drainer inserts the parent,
// uploads each photo, and inserts the responses with the resolved paths.
// ---------------------------------------------------------------------------
// The retry/bookkeeping fields every enqueue* stamps onto a fresh entry.
function baseEntryMeta(): Pick<BaseEntry, 'attempted_at' | 'attempt_count' | 'last_error' | 'failed' | 'queued_at'> {
  const now = new Date().toISOString()
  return { attempted_at: now, attempt_count: 0, last_error: null, failed: false, queued_at: now }
}

// Persist a fresh queue entry + wake any open list.
async function persistNewEntry(row: Entry): Promise<void> {
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

export async function enqueueQc(input: {
  runPayload: Record<string, unknown>
  responses:  QcResponseInQueue[]
  complete?:  boolean
}): Promise<void> {
  await persistNewEntry({
    id:         newUuid(),
    kind:       'qc',
    runPayload: input.runPayload,
    responses:  input.responses,
    complete:   input.complete ?? true,
    ...baseEntryMeta(),
  })
}

// The photo-resolve loop + atomic save is inherently branchy; the P2.2 rewrite
// REMOVED three DB-write blocks (it's simpler than before), so this complexity
// is inherited, not introduced. It mirrors the sibling run*Insert helpers.
// fallow-ignore-next-line complexity
async function runQcInsert(input: {
  db?: SupabaseClient,
  runPayload: Record<string, unknown>
  responses:  QcResponseInQueue[]
  onProgress?: (msg: string) => void
  complete?:  boolean
}): Promise<void> {
  const db = input.db ?? supabase
  const progress = input.onProgress ?? (() => {})
  const complete = input.complete ?? true

  const qcRunId = input.runPayload.qc_run_id as string

  // P2.2 (offline audit) — mig-611 ordering, now ONE code path for the live save
  // AND the drain. The qc_runs row is created only by the atomic save_qc_run RPC
  // at the end, AFTER every photo has uploaded, so a failed upload leaves NO
  // orphaned IN_PROGRESS run. (Replaced a pre-611 ordering that upserted qc_runs
  // FIRST, then uploaded, then inserted responses + flipped COMPLETED as three
  // separate writes — the regression the audit flagged.)
  //
  // 1. Resolve each response's photo path. photo_url short-circuits (pre-uploaded
  // at capture); a Blob is uploaded under qc/runs/<qc_run_id>/<item_id>.<ext> —
  // keyed on item_id (stable) so a replay overwrites the same object, no orphan.
  const responsePayload: {
    item_id:        string
    answer_text:    string | null
    answer_number:  number | null
    answer_options: string[] | null
    photo_url:      string | null
  }[] = []
  const totalPhotos = input.responses.filter(r => !r.photo_url && r.photo).length
  let photoIndex = 0
  for (const r of input.responses) {
    let photoPath: string | null = r.photo_url ?? null
    if (!photoPath && r.photo) {
      photoIndex += 1
      const ext  = (r.photo_name?.split('.').pop() ?? 'jpg').toLowerCase()
      const path = await tenantPathResolved(`qc/runs/${qcRunId}/${r.item_id}.${ext}`)
      // Re-compress only if suspiciously large (raw camera bytes — capture-time
      // compression threw); QC.tsx normally hands us ~130 KB already.
      let blobToUpload: Blob = r.photo
      if (r.photo.size > 500_000) {
        progress(`Compressing photo ${photoIndex}/${totalPhotos} (${(r.photo.size/1024/1024).toFixed(1)} MB raw)…`)
        blobToUpload = await compressStandard(r.photo as File)
      }
      progress(`Uploading photo ${photoIndex}/${totalPhotos} (${(blobToUpload.size/1024).toFixed(0)} KB)…`)
      // BARE upload (no Promise.race wrapper), matching QC.tsx's proven Tab-S9
      // path — the wrapped uploadWithTimeout stalled there. DELIBERATE
      // exception to the bounding applied to the other drain uploads (audit
      // #17): a real device incident beats a theoretical hang, and the
      // drain-level withTimeout now caps this entry's wallclock regardless. upsert:true so a
      // replay overwrites the same stable path; a failure propagates so the
      // drainer retries the whole entry (mig-465 cron reaps any loose object,
      // and no run row exists yet to orphan — it's created only by the RPC).
      const { error: upErr } = await storageFrom('delivery-photos', db)
        .upload(path, blobToUpload, { upsert: true, contentType: r.photo.type || 'image/jpeg' })
      if (upErr) throw dbError(upErr)
      photoPath = path
    }
    const isEmpty =
      r.answer_text == null && r.answer_number == null &&
      (r.answer_options?.length ?? 0) === 0 && !photoPath
    if (isEmpty) continue
    responsePayload.push({
      item_id:        r.item_id,
      answer_text:    r.answer_text,
      answer_number:  r.answer_number,
      answer_options: r.answer_options,
      photo_url:      photoPath,
    })
  }

  // 2. One atomic round-trip (mig 611): save_qc_run creates the qc_runs row +
  // inserts every response + flips to COMPLETED (firing the mig-176 result
  // trigger) in a SINGLE transaction — all-or-nothing, so a mid-sequence failure
  // leaves nothing behind. jsonb_to_record ignores the extra qc_run_id key on
  // p_run and forces noted_by / started_by / completed_by to the caller.
  progress(complete ? 'Saving…' : 'Saving draft…')
  const { error: saveErr } = await db.rpc('save_qc_run', {
    p_qc_run_id: qcRunId,
    p_run:       input.runPayload,
    p_responses: responsePayload,
    p_complete:  complete,
  })
  // A 23505 = this run already committed on a prior (lost-response) attempt.
  // save_qc_run is now upsert-based (mig 20260707190000) so a replay is a
  // clean no-op, but keep tolerating a stray 23505 from an in-flight older
  // client — it still means idempotent success, NOT a failure that would
  // strand a succeeded QC run as "failed" in the queue (the W1 class).
  if (saveErr && !isUniqueViolation(saveErr)) throw dbError(saveErr)
}

export async function insertOrQueueQc(input: {
  db?: SupabaseClient
  runPayload: Record<string, unknown>
  responses:  QcResponseInQueue[]
  onProgress?: (msg: string) => void
  /** false = save an IN_PROGRESS draft; omitted/true = complete the run (R3). */
  complete?:  boolean
}): Promise<'sent' | 'queued'> {
  const progress = input.onProgress ?? (() => {})
  const complete = input.complete ?? true
  // Mint qc_run_id up front so the live attempt + any queued retry share it —
  // runQcInsert calls the (upsert) save_qc_run RPC keyed on it, so a retry (or
  // a resumed draft, which passes its own qc_run_id) never duplicates the run.
  if (!input.runPayload.qc_run_id) input.runPayload.qc_run_id = newUuid()
  if (offline() || !(await supabaseReachable())) {
    progress('Network unreachable — queuing for sync…')
    await enqueueQc({ runPayload: input.runPayload, responses: input.responses, complete })
    return 'queued'
  }
  try {
    // Larger wallclock cap than other inserts — a QC run with several
    // photos legitimately takes longer than a single-photo insert. We
    // still bail out into the queue before the operator gives up.
    await withTimeout(
      runQcInsert({ db: input.db, runPayload: input.runPayload, responses: input.responses, onProgress: input.onProgress, complete }),
      LIVE_ATTEMPT_TIMEOUT_MS * 4, 'qc insert',
    )
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      progress('Network failure — queuing for retry…')
      await enqueueQc({ runPayload: input.runPayload, responses: input.responses, complete })
      return 'queued'
    }
    throw e
  }
}

// M32: the latest queued (undrained) QC DRAFT for a check. Offline, a saved
// draft lives ONLY in this queue — the DB resume query finds nothing, so
// without this the reopen mints a fresh run id and the eventual drain creates
// an orphaned IN_PROGRESS run beside a duplicate completed one. complete=true
// entries are finished checks, not resumable drafts. The queue is
// device-local IndexedDB, so its drafts are this device's operator's.
// A delivery check (mig 20261185000000) keys on source_delivery_id instead, and
// a pallet check (mig 20261296000000) on source_delivery_pallet_id. A delivery
// key never matches a pallet's draft on that delivery.
export function findQueuedQcDraft(
  entries: Entry[], sourceId: string, templateId: string,
  key: 'source_run_id' | 'source_delivery_id' | 'source_delivery_pallet_id' = 'source_run_id',
): QcEntry | null {
  const drafts = entries.filter((e): e is QcEntry =>
    e.kind === 'qc' && e.complete === false
    && e.runPayload[key] === sourceId
    && (key !== 'source_delivery_id' || !e.runPayload.source_delivery_pallet_id)
    && e.runPayload.template_id === templateId
    && typeof e.runPayload.qc_run_id === 'string')
  if (drafts.length === 0) return null
  return drafts.reduce((a, b) => (a.queued_at > b.queued_at ? a : b))
}

// ---------------------------------------------------------------------------
// Delivery (gate intake — new-delivery shape only). On drain: upload the note
// photo and the load photo (keyed on the client-minted delivery_id), then one
// atomic + idempotent save_gate_intake_delivery RPC (mig 615) writes deliveries
// + delivery_additions + status_history, then the load photo's path is set on
// the delivery (the RPC has no column for it). The drainer handles a missing
// photo blob (operator skipped one) by passing photo_url=null.
// ---------------------------------------------------------------------------
interface DeliveryInput {
  deliveryPayload: Record<string, unknown>
  additionPayload: Record<string, unknown>
  newStatus:       string
  photo:           Blob | null      // the delivery-note photo
  photo_name:      string | null
  load_photo?:      Blob | null     // the "Take photo of load" gate photo
  load_photo_name?: string | null
}

/** What happened to the photos of a delivery that DID save. A photo that
 *  failed for a reason a retry won't fix (storage refused it, too large, not a
 *  decodable image) is dropped and the delivery saves without it. Before, that
 *  answered a plain 'sent', exactly like a clean save, so the gate was never
 *  told (gate-intake data-loss S4). */
export interface DeliverySendResult {
  status:         'sent' | 'queued'
  notePhotoError: string | null
  loadPhotoError: string | null
}

export async function enqueueDelivery(input: DeliveryInput): Promise<void> {
  const row: DeliveryEntry = {
    id:              newUuid(),
    kind:            'delivery',
    deliveryPayload: input.deliveryPayload,
    additionPayload: input.additionPayload,
    newStatus:       input.newStatus,
    load_photo:      input.load_photo ?? null,
    load_photo_name: input.load_photo_name ?? null,
    photo:           input.photo,
    photo_name:      input.photo_name,
    attempted_at:    new Date().toISOString(),
    attempt_count:   0,
    last_error:      null,
    failed:          false,
    queued_at:       new Date().toISOString(),
  }
  await withStore('readwrite', s => s.add(row))
  notifyChanged()
}

// Upload one gate photo for a delivery. A network-shaped failure throws, so
// the whole entry is retried; any other failure is returned as its message and
// the caller saves without the photo, saying so.
async function uploadDeliveryPhoto(
  db: SupabaseClient, deliveryId: string, kind: 'note' | 'load', photo: Blob, photoName: string | null,
): Promise<{ path: string | null; error: string | null }> {
  try {
    const ext        = (photoName?.split('.').pop() ?? 'jpg').toLowerCase()
    const path       = await tenantPathResolved(`${deliveryId}/${kind}-${Date.now()}.${ext}`)
    const compressed = await compressStandard(photo as File)
    const { error: upErr } = await storageFrom('delivery-photos', db)
      .upload(path, compressed, { upsert: true, contentType: photo.type || 'image/jpeg' })
    if (upErr) throw upErr
    return { path, error: null }
  } catch (e) {
    const err = asError(e)
    if (isTransientFault(err.message, (err as { code?: string | null }).code ?? null)) throw err
    return { path: null, error: err.message }
  }
}

// A storage / PostgREST error can arrive as a plain { message, code } object,
// not an Error: String() of it reads "[object Object]" and the transient
// check then misses a network failure. Normalise, keeping the SQLSTATE.
function asError(e: unknown): Error {
  if (e instanceof Error) return e
  const o = e as { message?: unknown; code?: string | null; details?: string | null } | null
  return dbError({ message: typeof o?.message === 'string' ? o.message : String(e), code: o?.code ?? null, details: o?.details ?? null })
}

// One branch per photo outcome, each a distinct thing the gate must be told
// about (see DeliverySendResult).
// fallow-ignore-next-line complexity
async function runDeliveryInsert(input: DeliveryInput & { db?: SupabaseClient }): Promise<Omit<DeliverySendResult, 'status'>> {
  const db = input.db ?? supabase
  // delivery_id + addition_id are minted by the caller (Deliveries.tsx) and
  // ride in the payloads, so a drain-retry replays with the SAME ids and the
  // mig-615 RPC's ON CONFLICT no-ops instead of creating a duplicate delivery
  // (the old server-generated id duplicated on every retry).
  //
  // Backward-compat: entries queued by the PRE-mig-615 client have no ids in
  // their payloads. Mint fresh ones here so they still drain (a brand-new id
  // can't collide) instead of inserting a NULL primary key and sticking in the
  // queue forever. Such legacy entries keep the old at-most-once-on-success
  // behaviour; only same-session retries of NEW entries get full idempotency.
  const deliveryId = (input.deliveryPayload.delivery_id as string | undefined) ?? newUuid()
  const additionId = (input.additionPayload.addition_id as string | undefined) ?? newUuid()

  // Upload the photos first (paths keyed on the minted id), so a photo that
  // cannot be stored is known BEFORE the delivery is written and the record
  // can say so in its notes. There is no control anywhere to re-attach a note
  // photo later, and on a drain nobody is watching, so the notes line is the
  // only trace a photo was ever taken.
  let deliveryPayload = input.deliveryPayload
  let additionPayload = input.additionPayload
  let notePath: string | null = null
  let notePhotoError: string | null = null
  if (input.photo) {
    const up = await uploadDeliveryPhoto(db, deliveryId, 'note', input.photo, input.photo_name)
    notePath = up.path
    notePhotoError = up.error
    if (up.error) {
      deliveryPayload = { ...deliveryPayload, notes: withPhotoLossNote(deliveryPayload.notes, 'Delivery-note photo') }
      additionPayload = { ...additionPayload, notes: withPhotoLossNote(additionPayload.notes, 'Delivery-note photo') }
    }
  }
  let loadPath: string | null = null
  let loadPhotoError: string | null = null
  if (input.load_photo) {
    const up = await uploadDeliveryPhoto(db, deliveryId, 'load', input.load_photo, input.load_photo_name ?? null)
    loadPath = up.path
    loadPhotoError = up.error
    if (up.error) deliveryPayload = { ...deliveryPayload, notes: withPhotoLossNote(deliveryPayload.notes, 'Load photo') }
  }

  // One atomic, idempotent round-trip (mig 615): deliveries + delivery_additions
  // + status_history. Was four sequential writes that orphaned a delivery on a
  // mid-sequence failure and duplicated it on retry.
  const { error } = await db.rpc('save_gate_intake_delivery', {
    p_delivery_id: deliveryId,
    p_addition_id: additionId,
    p_delivery:    { ...deliveryPayload, delivery_note_photo_url: notePath },
    p_addition:    { ...additionPayload, delivery_note_photo_url: notePath },
    p_new_status:  input.newStatus,
  })
  if (error) throw dbError(error)

  // The load photo's path goes on the delivery the RPC just wrote. A network
  // failure throws: the entry is retried, the RPC no-ops on its minted id and
  // this step runs again. Anything else is reported back (the live gate says
  // so) and, on a drain, to Sentry, since the row is already written and its
  // notes can't be amended by a write that is itself being refused.
  if (loadPath) {
    try {
      await patchDeliveryAtCurrentVersion(db, deliveryId, {
        scale_photo_url: loadPath,
        update_reason:   'gate intake — load photo',
      })
    } catch (e) {
      const err = asError(e)
      if (isTransientFault(err.message, (err as { code?: string | null }).code ?? null)) throw err
      loadPhotoError = err.message
      void reportError(err, { source: 'manual', level: 'warning', context: { what: 'delivery load photo not attached', delivery_id: deliveryId } })
    }
  }
  return { notePhotoError, loadPhotoError }
}

export async function insertOrQueueDelivery(input: DeliveryInput & { db?: SupabaseClient }): Promise<DeliverySendResult> {
  const queued: DeliverySendResult = { status: 'queued', notePhotoError: null, loadPhotoError: null }
  // Skip the up-to-2s supabaseReachable() probe on this gate hot-path (same as
  // spray): attempt directly, bounded by LIVE_ATTEMPT_TIMEOUT_MS*4, and fall
  // back to the queue on any network-ish failure below.
  if (offline()) {
    await enqueueDelivery(input)
    return queued
  }
  try {
    // Same generous wallclock as QC — a single photo over slow Wi-Fi
    // can chew the whole window; better to queue and let the operator
    // move on than to keep them at the gate.
    const photos = await withTimeout(runDeliveryInsert(input), LIVE_ATTEMPT_TIMEOUT_MS * 4, 'delivery insert')
    return { status: 'sent', ...photos }
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueueDelivery(input)
      return queued
    }
    throw e
  }
}

export async function insertOrQueuePallet(rawInput: QueuePalletInput & { db?: SupabaseClient }): Promise<'sent' | 'queued'> {
  // Mint the fruit_tokens id once, up front, so the live attempt AND any queued
  // retry share it → the drain dedupes (upserts the token, skips the pallet if
  // one already exists for it) instead of creating a duplicate token + pallet.
  // Strip `db` from the queued shape — a SupabaseClient isn't IDB-serializable.
  const { db, ...rest } = rawInput
  const input = { ...rest, token_id: rest.token_id ?? newUuid() }
  // Reachability pre-flight — much faster than waiting for the live attempt
  // to time out. If Supabase does not answer a 2s HEAD, queue immediately.
  if (offline() || !(await supabaseReachable())) {
    await enqueuePallet(input)
    return 'queued'
  }
  // Reachable per the ping, so try the live attempt. Wallclock-cap as a
  // backup in case the ping passed but POST hangs (e.g., quota / rate-limit).
  // `db` (optional) lets the caller pass an isolated supabase client so the
  // live attempt skips the navigator.locks auth-token contention with the
  // page's other useQuery hooks. The queue drainer runs from background
  // and uses the shared client by default.
  try {
    await withTimeout(runPalletCreate({ ...input, db }), LIVE_ATTEMPT_TIMEOUT_MS, 'pallet create')
    return 'sent'
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e)
    if (isTransientFault(msg)) {
      await enqueuePallet(input)
      return 'queued'
    }
    throw e
  }
}

// ---------------------------------------------------------------------------
// runPalletCreate — performs the multi-step pallet create. Used by both the
// direct-online path and the queue drainer.
//
// Steps (must be in this order due to FK constraints):
//   1. Create fruit_token (returns token_id)
//   2. Generate pallet_id via RPC
//   3. Upload photo to Storage (if any) — failure here just sets photo to null
//      so the pallet still gets created (we can't reattempt the upload later
//      without re-prompting the operator).
//   4. Insert pallet row referencing the token + photo path
// ---------------------------------------------------------------------------
async function runPalletCreate(input: QueuePalletInput & { db?: SupabaseClient }): Promise<void> {
  // Use the caller-provided isolated client if any (live-path from a
  // busy page) or fall back to the shared client (queue-drain runs
  // from the background flusher and doesn't contend with page hooks).
  const db = input.db ?? supabase
  const tokenId = input.token_id ?? newUuid()

  // 1. fruit_token with the CLIENT-MINTED id — upsert-ignore so a replay reuses
  // the same token instead of creating a duplicate.
  const { error: tokenErr } = await db.from('fruit_tokens').upsert({
    token_id:   tokenId,
    grower:     input.grower,
    variety:    input.variety,
    puc:        input.puc ?? null,
    run_id:     input.run_id,
    created_by: input.packed_by,
  }, { onConflict: 'token_id', ignoreDuplicates: true })
  // dbError, not a bare throw: supabase-js only builds a real PostgrestError
  // when shouldThrowOnError is set — otherwise `{ error }` is a PLAIN OBJECT,
  // and a mid-flight network drop yields `{ message: "TypeError: Failed to
  // fetch", code: "" }`. Thrown raw it stringifies to "[object Object]", which
  // matches no classifier: the live path calls it non-transient and does NOT
  // queue the pallet (the capture is simply lost, on the packing line's core
  // flow), and the drain counts it a HARD server rejection, so minutes of
  // flaky Wi-Fi mark the entry failed and show the operator "[object Object]".
  // Same M120 fix every other write in this file already carries.
  if (tokenErr) throw dbError(tokenErr)

  // 2. Idempotency guard. pallet_id is a server-minted sequential id (can't be
  // client-minted), so we can't upsert the pallet. Instead, key idempotency on
  // token_id: if a pallet already exists for this token (a prior live attempt
  // landed before its response was lost), this is a replay — stop. Safe without
  // a DB unique constraint because the queue drains sequentially (no two
  // concurrent inserts race this check).
  const { data: existing, error: existErr } = await db.from('pallets')
    .select('pallet_id').eq('token_id', tokenId).limit(1).maybeSingle()
  if (existErr) throw dbError(existErr)
  if (existing) return

  const { data: idData, error: idErr } = await db.rpc('generate_pallet_id')
  if (idErr) throw dbError(idErr)
  const palletId = idData as string

  let photoPath: string | null = null
  if (input.photo) {
    // Key on token_id (stable) so a replay overwrites the same object.
    const ext  = (input.photo_name?.split('.').pop() ?? 'jpg').toLowerCase()
    const path = await tenantPathResolved(`pallets/${input.run_id}/${tokenId}.${ext}`)
    // Compress before upload — pallet scale photos only need the readout
    // to be legible, and on a flaky link a 4 MB raw upload often times out
    // and the operator falls back here without a photo. Compression takes
    // it to ~300 KB.
    const compressed = await safeCompress(input.photo)
    const { error: upErr } = await storageFrom('delivery-photos', db)
      .upload(path, compressed, { upsert: true, contentType: 'image/jpeg' })
    if (!upErr) photoPath = path
    // If the upload fails (network), we still proceed without a photo —
    // losing the pallet would be much worse than losing the photo. The
    // operator can re-attach later.
  }

  const { error: palletErr } = await db.from('pallets').insert({
    pallet_id:       palletId,
    token_id:        tokenId,
    run_id:          input.run_id,
    packed_by:       input.packed_by,
    scale_photo_url: photoPath,
    ...input.fields,
  })
  // W3 (offline audit 2026-06-30) — the SELECT-then-INSERT above is now backed
  // by a DB UNIQUE(token_id) WHERE deleted_at IS NULL (mig 20260702130000). A
  // concurrent create the in-process drain mutex can't span (another tab, the
  // same device's live-attempt racing a drain, or another device) collides on
  // that index instead of minting a second pallet for one token. That 23505 is
  // an idempotent replay, not a failure — the pallet already exists, so treat
  // it as success rather than letting isPermanent4xx strand a succeeded pallet
  // as "failed" in the queue.
  if (palletErr && !isUniqueViolation(palletErr)) throw dbError(palletErr)
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
// Error / network / RLS classifiers were extracted to ./offlineQueue/classify
// (Phase 3b, step 1 — now unit-tested). Imported above for internal use;
// re-export isRlsDenial so existing '@/lib/offlineQueue' importers keep working.
export { isRlsDenial }

// Wallclock cap on ONE drained entry. drainQueueInner is a strictly sequential
// `for (const entry of entries)` loop, so an entry that black-holes parks every
// entry behind it. Most of these helpers do Storage uploads, and the Storage
// abort net is STORAGE_TIMEOUT_MS = 6 min (supabase.ts) — not the 30s REST net.
// A tablet reconnecting with a three-photo backlog could therefore sit ~18
// minutes showing "N queued" with nothing moving, while the 60s flusher's ticks
// were coalesced into the same stuck run by the single-flight guard. The drain's
// own comment claimed this was closed (finding M2) when only six of 23 branches
// were actually wrapped.
//
// Deliberately much longer than LIVE_ATTEMPT_TIMEOUT_MS (8s): the live path can
// afford to give up early because it just queues instead, whereas here giving
// up means the entry waits for the next flush. Two minutes is generous for a
// compressed (~300 KB) photo on a bad link and still bounds the stall.
const DRAIN_SEND_TIMEOUT_MS = 120_000

const MAX_DRAIN_ATTEMPTS = 5

// An RLS / permission denial on the drain won't heal on blind retry — the
// caller's role or active tenant has to change (field case 2026-07-04: a
// FARM_WORKER's offline spray completion looped as "Load failed" until an admin
// fixed the role). But a SINGLE denial can be a tenant-switch race (the session's
// active tenant momentarily wrong), which self-heals — so we give it one retry,
// then, once it persists, mark it failed with a clear, actionable message. Once
// marked failed the entry is KEPT (never deleted) + skipped by future drains, so
// a later network blip can't overwrite the reason with a misleading "Load failed".
const RLS_DENIAL_MAX_ATTEMPTS = 2
const RLS_DENIAL_MESSAGE =
  "You don't have permission to sync this — ask an admin to check your role."
const SIGNED_OUT_MESSAGE =
  'Waiting for you to sign in — it sends by itself once you do.'

// One-time rescue for entries the drain failed BEFORE it waited for a session
// (Magalies, 30 Sep 2026). A phone left on the login screen overnight sent its
// queue with no session; the server refused each write twice and the entry was
// parked as failed under RLS_DENIAL_MESSAGE — "ask an admin to check your
// role" — so scouts and sprayers read their captures as gone and some tapped
// Clear. Every such entry gets ONE fresh try, the first time this device drains
// signed in. A genuine role refusal simply fails again after its grace; that
// costs two refused requests, once per device. Remembered per device so it
// never re-arms an entry the operator has since chosen to leave parked.
const RLS_REVIVE_KEY = 'kf49.offlineQueue.revivedSignedOutDenials.v1'
async function reviveSignedOutDenialsOnce(): Promise<void> {
  try { if (window.localStorage.getItem(RLS_REVIVE_KEY)) return } catch { return }
  const parked = (await listPending()).filter(e => e.failed && e.last_error === RLS_DENIAL_MESSAGE)
  if (parked.length) await rearm(parked)
  try { window.localStorage.setItem(RLS_REVIVE_KEY, new Date().toISOString()) } catch { /* retried next drain */ }
}

// Two guards on every live attempt:
//   1. A 2-second reachability ping to Supabase before we commit. Catches
//      "Wi-Fi associated but DNS dead" and "captive portal sitting in front
//      of us" without waiting for the slow path.
//   2. An 8-second wallclock on the live attempt itself, as backup if the
//      ping somehow lies (e.g., reachable for HEAD but not POST).
// Both bail out into the existing queue path so the operator never sees a
// stuck "Creating…" form.
const REACHABILITY_TIMEOUT_MS = 2_000
const LIVE_ATTEMPT_TIMEOUT_MS = 8_000

function withTimeout<T>(p: PromiseLike<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new Error(`${label} timeout`)), ms)
    Promise.resolve(p).then(
      v => { clearTimeout(id); resolve(v) },
      e => { clearTimeout(id); reject(e)  },
    )
  })
}

// Quick HEAD against the Supabase REST root. We only care that something
// answers within REACHABILITY_TIMEOUT_MS. Any 2xx/3xx/4xx counts; only a
// fetch rejection (DNS, TLS, abort) or 5xx fails.
async function supabaseReachable(): Promise<boolean> {
  if (offline()) return false
  const base = (import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/$/, '')
  if (!base) return true   // no URL configured — fall through to legacy behaviour
  try {
    const ctrl = new AbortController()
    const id   = setTimeout(() => ctrl.abort(), REACHABILITY_TIMEOUT_MS)
    const res  = await fetch(`${base}/rest/v1/`, { method: 'HEAD', signal: ctrl.signal, mode: 'cors' })
    clearTimeout(id)
    return res.status < 500
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// A permanently-stranded offline write (permanent 4xx or retry cap exhausted)
// is operator data that will now NEVER land — and across 5,000 field users that
// was completely invisible (scale audit OP-5). Report it so it surfaces in
// Sentry / client_errors with a stuck-depth metric; money kinds (piece-work +
// labour pay) escalate to error level. Telemetry must never throw in the drain.
// 'pick' belongs here even though the pick row itself carries no rand: a
// stranded pick strands every tally under it (the drain lands parent-first),
// so the money loss is real — it just lives one row down.
const MONEY_KINDS = new Set(['pick', 'pick_tallies', 'packout_drops', 'labour_attest', 'labour_adjust', 'span_clock', 'harvest_load', 'harvest_general', 'ppe_issue'])
async function reportStrandedWrite(entry: { kind: string; attempt_count: number; hard_attempts?: number }, err: Error): Promise<void> {
  try {
    const stuckDepth = (await listPending()).filter(e => e.failed).length
    reportError(err, {
      source: 'manual',
      level:  MONEY_KINDS.has(entry.kind) ? 'error' : 'warning',
      // force past the operator-noise swallow (M72): a permanently-stranded write
      // has already exhausted every retry, so the raw DB text (e.g. "Worker F329
      // not found in current tenant" on a renumbered-worker pick_tallies drop) is
      // lost-pay telemetry, not live-operator noise — it must reach Sentry /
      // client_errors even though it matches a DB_VALIDATION_PATTERN.
      force: true,
      context: {
        event:       'offline_write_stranded',
        kind:        entry.kind,
        attempts:    entry.attempt_count,
        // Both numbers, because the GAP between them is the diagnosis: 38 total
        // vs 1 hard = a device on bad signal that met one rejection, not a write
        // the server refused 38 times.
        hard_attempts: entry.hard_attempts ?? 0,
        stuck_depth: stuckDepth,
      },
    })
  } catch { /* telemetry must never break the drain path */ }
}

// Drain — attempts to flush every queued entry once. Returns counts.
// Stops early on the first network failure (we're probably offline again).
//
// W2 (offline audit 2026-06-30 §2.1) — single-flight guard. drainQueue is
// kicked from the 60s interval, the `online` event (installQueueFlusher's
// start() AND its handler both fire it), retryQueueEntry, and the badge Sync
// button. Two overlapping runs each listPending() the SAME rows and insert
// before either deletes → duplicate server rows for the non-idempotent kinds
// (W1 tipping / W3 pallet). The audit reproduced 2 POSTs for 1 queued entry on
// a single reconnect. We serialise so at most one drain body runs at a time; a
// call that arrives mid-drain sets a flag so exactly one more pass runs
// afterwards (so an entry queued/retried during a drain isn't stranded until
// the next 60s tick). Coalesced callers share the in-flight result — safe
// because every caller is fire-and-forget or spinner-only (none read counts).
// ---------------------------------------------------------------------------
let drainInFlight: Promise<{ sent: number; failed: number }> | null = null
let drainQueuedAgain = false

// W6 (offline audit) — exponential backoff with jitter after a TRANSIENT drain
// failure (network blip, expired-JWT reconnect, 5xx, or a PT429 rate-limit).
// Without it a sustained outage burns the 5-retry cap in ~5 min (5 × the 60s
// interval → the write is markFailed'd and needs a manual retry), and reconnect
// flaps re-drain with no spacing. Drain-level, not per-entry: one transient
// break arms it, the interval/`online` drains respect it, and a successful send
// OR an explicit operator retry clears it. Honours the rate limiter's
// "Retry after ~N s" hint as a floor (mig 20260701220500 — a PostgREST RAISE
// has no Retry-After header, so the hint is text-only).
const DRAIN_BACKOFF_BASE_MS = 30_000
const DRAIN_BACKOFF_MAX_MS  = 15 * 60_000
let drainBackoffUntil = 0
let drainBackoffStep  = 0

function armDrainBackoff(msg: string, details?: string | null): void {
  drainBackoffStep += 1
  const exp    = Math.min(DRAIN_BACKOFF_MAX_MS, DRAIN_BACKOFF_BASE_MS * 2 ** (drainBackoffStep - 1))
  const jitter = exp * 0.2 * Math.random()          // ±20% so a fleet doesn't retry in lockstep
  // The limiter puts "retry after ~N s" in DETAIL; fall back to the message.
  const hinted = retryAfterMs(details ?? '') ?? retryAfterMs(msg) ?? 0
  drainBackoffUntil = Date.now() + Math.max(hinted, exp + jitter)
}

// Clear the drain backoff — on a successful send, and when an operator forces a
// sync (retryQueueEntry / the badge's "Sync" button) so their explicit action
// is never silently swallowed by an active backoff window.
export function clearDrainBackoff(): void {
  drainBackoffStep  = 0
  drainBackoffUntil = 0
}

export function drainQueue(): Promise<{ sent: number; failed: number }> {
  if (drainInFlight) {
    drainQueuedAgain = true
    return drainInFlight
  }
  const spawn = (): Promise<{ sent: number; failed: number }> => {
    drainQueuedAgain = false
    return drainQueueInner().finally(() => {
      drainInFlight = drainQueuedAgain ? spawn() : null
    })
  }
  drainInFlight = spawn()
  return drainInFlight
}

/** Test seam: resolve once no drain is running — Retry and retryAllFailed start
 *  one fire-and-forget, and a test must not end with it still reading the queue. */
export async function _drainSettledForTests(): Promise<void> {
  while (drainInFlight) await drainInFlight
}

// drainQueueInner is the pre-existing 182-line sequential drain loop (one branch
// per queue kind). Its complexity is INHERITED, not introduced here: the W2
// single-flight wrap above only renamed it from `drainQueue`, adding no
// branching. Splitting the load-bearing drain isn't in scope for a P0 fix.
// fallow-ignore-next-line complexity
// Shared drain-failure disposition for a persistent RLS / permission denial.
// One retry absorbs a tenant-switch race; on the next denial the entry is marked
// permanently failed (KEPT, with a clear message + a Retry button) rather than
// left to loop. Deliberately does NOT reportStrandedWrite — an RLS denial is an
// operator/role setup issue, not an engineering bug to page (same stance as the
// live path's friendly ValidationError, which classify.ts keeps out of Sentry).
// Returns true when it marked the entry failed so the caller can bump `failed`.
function sessionLive(): boolean {
  if (!hasStoredSession()) return false
  const exp = decodeStoredClaims()?.raw?.exp
  return !(typeof exp === 'number' && exp * 1000 <= Date.now())
}

async function disposeRlsDenial(entry: Entry): Promise<boolean> {
  // Signed out mid-drain, or the stored token has expired (a refresh that
  // failed on a retryable error leaves it on disk while supabase-js sends on
  // the anon key): the refusal is about the session, not this entry or anyone's
  // role. Note it, spend nothing, and let the next drain with a live session
  // send it (see the gate at the top of drainQueueInner).
  if (!sessionLive()) {
    await recordFailure(entry, new Error(SIGNED_OUT_MESSAGE))
    return false
  }
  const friendly = new Error(RLS_DENIAL_MESSAGE)
  // hardAttempts, not attempt_count: an RLS denial IS the server answering, but
  // the grace it's counting is "one more denial", not "one more anything". Gated
  // on the total, a device with a week of network blips behind it got no grace
  // at all and was failed on its first denial.
  if (hardAttempts(entry) + 1 >= RLS_DENIAL_MAX_ATTEMPTS) {
    await markFailed(entry, friendly)
    return true
  }
  await recordFailure(entry, friendly, { hard: true })
  return false
}

/**
 * Should this entry WAIT rather than send, because the user is signed in to a
 * different tenant than the one it was captured on?
 *
 * The rule the spray, pick_tallies and packout_drops arms each spell out by
 * hand, named once. It is deliberately conservative in both directions:
 *   * an entry with no stamp (queued before its kind carried one) drains
 *     best-effort, because refusing it would strand real work;
 *   * an unknown active tenant (cold boot, no claim yet) also drains, matching
 *     the pre-guard behaviour rather than blocking on a claim that may never
 *     arrive.
 * Deferring is NOT failing: the entry is kept, its attempt_count is untouched,
 * and it goes out whole the next time the user is in its own tenant.
 */
function defersToOtherTenant(entry: { tenant_id?: string | null }): boolean {
  const active = decodeStoredClaims()?.active_tenant_id ?? null
  return !!entry.tenant_id && !!active && entry.tenant_id !== active
}

// fallow-ignore-next-line complexity  (inherent per-kind dispatch giant, already CRITICAL pre-change; the 2 RLS-denial branches added here are minimal + consistent with the existing failure-handling — cutting this function's CRAP is a separate refactor, not this fix. Same posture as the sibling run* fns in this file.)
async function drainQueueInner(): Promise<{ sent: number; failed: number }> {
  if (offline()) return { sent: 0, failed: 0 }
  // W6 — respect an active backoff window armed by a prior transient failure.
  // Operator-forced syncs clear it first (clearDrainBackoff), so this only gates
  // the automatic interval / `online`-event drains, never an explicit retry.
  if (Date.now() < drainBackoffUntil) return { sent: 0, failed: 0 }
  // No session on the device = every write goes out on the anon key, and the
  // server refuses it (every queue kind, station RPCs included, is
  // authenticated-only). That refusal used to count against the entry: a phone
  // left online on the login screen overnight failed its whole queue before
  // anyone signed in (Magalies, 30 Sep 2026). Wait instead — the next drain
  // after sign-in sends it. An expired access token with a refresh token still
  // on disk passes this gate (supabase-js refreshes it on the first call when
  // it can); a refusal while it is still expired is not counted, in
  // disposeRlsDenial.
  if (!hasStoredSession()) return { sent: 0, failed: 0 }
  await reviveSignedOutDenialsOnce()

  const entries = await listPending()
  // Drain in queue-time order so the oldest backlog goes first.
  entries.sort((a, b) => a.queued_at.localeCompare(b.queued_at))

  let sent = 0, failed = 0
  for (const entry of entries) {
    // Council T3.8 — skip entries already marked permanently failed. They
    // stay in the queue for operator triage; a fresh drain doesn't retry
    // (and doesn't re-count) them.
    if (entry.failed) continue
    try {
      if (entry.kind === 'tipping') {
        // withTimeout like every other kind's live send (finding M2,
        // 2026-06-11): this was the one unwrapped send in the drain, and
        // drainQueue is a sequential loop — a single black-holed request
        // stalled the WHOLE drain, so nothing synced and the post-drain
        // invalidateQueries never fired ("lists stay stale until refresh").
        // sendTip links the run first when the entry asks (a Yard tip).
        const { error } = await withTimeout(
          sendTip(entry.payload, !!entry.link_run_delivery),
          LIVE_ATTEMPT_TIMEOUT_MS,
          'tipping drain insert',
        )
        // A PK collision = this tip already committed on a prior (lost-response)
        // attempt. Treat as success: fall through to deleteEntry, NOT markFailed
        // (isPermanent4xx would otherwise strand a succeeded tip as "failed").
        if (error && !isUniqueViolation(error)) {
          const code = (error as { code?: string | null }).code
          const e2 = dbError(error)
          if (isTransientFault(error.message, code)) {
            await recordFailure(entry, e2)
            armDrainBackoff(error.message, (error as { details?: string | null }).details)   // W6
            break
          }
          // A persistent permission denial won't heal on retry — surface it
          // clearly instead of looping (where a later network blip masks it as
          // "Load failed"). One retry first, to absorb a tenant-switch race.
          if (isRlsDenial(error.message)) {
            if (await disposeRlsDenial(entry)) failed += 1
            continue
          }
          // Council T3.8 — non-network failure: KEEP the row. Mark failed
          // only when it's a permanent payload fault or we've exhausted the
          // retry cap; otherwise record + keep for the next drain. Never
          // silent-delete (a transient RLS/5xx used to destroy the write).
          // The cap counts HARD attempts only — see hardAttempts().
          if (isPermanent4xx(error.message, code) || hardAttempts(entry) + 1 >= MAX_DRAIN_ATTEMPTS) {
            await markFailed(entry, e2)
            await reportStrandedWrite(entry, e2)
            failed += 1
          } else {
            await recordFailure(entry, e2, { hard: true })
          }
          continue
        }
      } else if (entry.kind === 'cooling_batch') {
        // FCX-16 — idempotent on the client-minted batch_id: a PK collision
        // means this dolly committed on a prior lost-response attempt.
        const { error } = await withTimeout(
          supabase.from('cooling_batches').insert(entry.payload),
          LIVE_ATTEMPT_TIMEOUT_MS,
          'cooling batch drain insert',
        )
        if (error && !isUniqueViolation(error)) throw dbError(error)
      } else if (entry.kind === 'cooling_transition') {
        // Tier 3 — replay the capture-time RPC. Global FIFO preserves per-
        // dolly order (a cool queued before its hold drains first). A replay
        // the state machine rejects (moved/cancelled meanwhile, capture out
        // of window) fails permanent and surfaces in the badge with the
        // trigger's own message — never silent loss, never a wrong write.
        const { error } = await withTimeout(
          supabase.rpc('apply_cooling_transition', entry.args),
          DRAIN_SEND_TIMEOUT_MS,
          'cooling transition drain',
        )
        if (error) throw dbError(error)
      } else if (entry.kind === 'batch_tip') {
        // Tier 3 — replay tip_cooling_batch (p_tipped_at carries the honest
        // time). An already-landed tip comes back as the original tipping_id
        // (BF-01, mig 20261032950000: same run, same caller) and clears here;
        // a batch tipped meanwhile by someone else or into another run is still
        // 'batch not RELEASED' → permanent + surfaced, never a double-count. A
        // role that lost tipping:edit gets 42501 "Permission denied: …" (BF-04)
        // → the RLS-denial path below: one grace retry, then failed and kept.
        const { error } = await withTimeout(
          supabase.rpc('tip_cooling_batch', entry.args),
          DRAIN_SEND_TIMEOUT_MS,
          'batch tip drain',
        )
        if (error) throw dbError(error)
      } else if (entry.kind === 'pallet_receipt') {
        // Tier 3 — replay receive_packed_pallets. Idempotent on the client-
        // minted p_receipt_id: a lost-response replay early-returns with the
        // already-minted numbers, never a duplicate receipt/run/pallets.
        const { error } = await withTimeout(
          supabase.rpc('receive_packed_pallets', entry.args),
          DRAIN_SEND_TIMEOUT_MS,
          'pallet receipt drain',
        )
        if (error) throw dbError(error)
      } else if (entry.kind === 'pallet') {
        // Pallet — multi-step creation (idempotent on the minted token_id)
        await withTimeout(runPalletCreate({
          token_id:   entry.token_id,
          run_id:     entry.run_id,
          grower:     entry.grower,
          variety:    entry.variety,
          puc:        entry.puc,
          packed_by:  entry.packed_by,
          fields:     entry.fields,
          photo:      entry.photo,
          photo_name: entry.photo_name,
        }), DRAIN_SEND_TIMEOUT_MS, 'pallet create drain')
      } else if (entry.kind === 'scouting') {
        // Tenant-attribution guard, same class as pick_tallies / spray below.
        // Without it the photo uploads into whatever tenant is ACTIVE (the
        // path is built from the live JWT) and the row is then refused by mig
        // 589, stranding the entry with a message that blames the scout's role.
        if (defersToOtherTenant(entry)) continue
        try {
          await withTimeout(runScoutingInsert({
            payload:      entry.payload,
            photo:        entry.photo,
            photo_name:   entry.photo_name,
            photoTries: entry.attempt_count,
            // Legacy entries (queued before multi-photo shipped) have no field.
            extra_photos: entry.extra_photos ?? [],
          }), DRAIN_SEND_TIMEOUT_MS, 'scouting insert drain')
        } catch (e) {
          // Kept, with the reason, for the person to see — never dropped.
          if (e instanceof ScoutPhotosRefused) {
            await markFailed(entry, e)
            failed += 1
            continue
          }
          throw e
        }
      } else if (entry.kind === 'farm_problem') {
        if (defersToOtherTenant(entry)) continue
        await withTimeout(runFarmProblemInsert({
          payload:    entry.payload,
          photo:      entry.photo,
          photo_name: entry.photo_name,
          photoTries: entry.attempt_count,
        }), DRAIN_SEND_TIMEOUT_MS, 'farm problem insert drain')
      } else if (entry.kind === 'trap_visit') {
        if (defersToOtherTenant(entry)) continue
        await withTimeout(runTrapVisitInsert({
          payload: entry.payload,
          photos:  entry.photos ?? [],
        }), DRAIN_SEND_TIMEOUT_MS, 'trap visit insert drain')
      } else if (entry.kind === 'security_incident') {
        // Tenant-attribution guard: the insert check and the photo bucket policy
        // compare the row's tenant to the active one. Defer, don't fail.
        if (defersToOtherTenant(entry)) continue
        await withTimeout(runSecurityIncidentInsert({
          payload: entry.payload,
          photos:  entry.photos ?? [],
        }), DRAIN_SEND_TIMEOUT_MS, 'security incident insert drain')
      } else if (entry.kind === 'scout_walk') {
        // A stranded walk is unrecoverable — no form in the app re-enters a GPS
        // track — so this guard matters more here than anywhere else.
        if (defersToOtherTenant(entry)) continue
        await withTimeout(runScoutWalkInsert({ payload: entry.payload }), DRAIN_SEND_TIMEOUT_MS, 'scout walk insert drain')
      } else if (entry.kind === 'pick') {
        // Tenant-attribution guard, same class as pick_tallies below. harvest_picks
        // has a RESTRICTIVE tenant_gate_insert (mig 332) that refuses a row whose
        // tenant_id isn't current_tenant_id(); the queued payload carries the origin
        // tenant. If the active tenant has changed since capture, draining now is an
        // RLS insert deny → this MONEY kind strands + pages AND its child pick_tallies
        // cascade into "Pick not found in current tenant" (mig 804). DEFER instead:
        // leave the entry pending (attempt_count untouched, not failed, not paged) so
        // it drains cleanly the next time the user is in the pick's origin tenant.
        // Only gates when BOTH the stamp and the active tenant are known and differ
        // (legacy unstamped entries fall through to best-effort drain).
        // A pick queued BEFORE the top-level stamp existed carries the origin tenant
        // only in its payload, so fall back to that rather than drain it blind.
        const pickPayloadTenant = typeof entry.payload?.tenant_id === 'string' ? entry.payload.tenant_id : null
        if (defersToOtherTenant({ tenant_id: entry.tenant_id ?? pickPayloadTenant })) continue
        await withTimeout(runPickInsert({
          payload:    entry.payload,
          photo:      entry.photo,
          photo_name: entry.photo_name,
          photoTries: entry.attempt_count,
        }), DRAIN_SEND_TIMEOUT_MS, 'pick insert drain')
      } else if (entry.kind === 'pick_tallies') {
        // Tenant-attribution guard. save_pick_tallies forces tenant_id =
        // current_tenant_id() and rejects a pick not in that tenant. If the
        // active tenant has changed since these drops were captured (tenant
        // switch, reload onto a different default, shared/multi-tenant login),
        // draining now would raise "Pick not found in current tenant" and — as a
        // MONEY_KIND — strand the pay data and page. DEFER instead: leave the
        // entry queued, don't count it failed, don't page. It drains cleanly when
        // the user is next in the pick's origin tenant. Only gates when BOTH the
        // stamp and the active tenant are known and differ (legacy unstamped
        // entries fall through to best-effort drain).
        const activeTid = decodeStoredClaims()?.active_tenant_id ?? null
        if (entry.tenant_id && activeTid && entry.tenant_id !== activeTid) continue
        await withTimeout(runPickTallies({
          pick_id:      entry.pick_id,
          tallies:      withCapturedAt(entry.tallies, entry.queued_at),
          device_token: entry.device_token,
          pick_date:    entry.pick_date,
          block_id:     entry.block_id,
        }), DRAIN_SEND_TIMEOUT_MS, 'pick tallies drain')
      } else if (entry.kind === 'packout_drops') {
        // Tenant-attribution guard, same class as pick_tallies above:
        // save_packout_drops forces tenant_id = current_tenant_id() and refuses
        // a packer who is not in that tenant, so a drain under a different
        // active tenant would strand a MONEY kind. DEFER instead — it drains
        // cleanly the next time the user is in the packhouse it was captured in.
        const packTid = decodeStoredClaims()?.active_tenant_id ?? null
        if (entry.tenant_id && packTid && entry.tenant_id !== packTid) continue
        await withTimeout(runPackoutDrops({
          drops:        entry.drops,
          device_token: entry.device_token,
        }), DRAIN_SEND_TIMEOUT_MS, 'packout drops drain')
      } else if (entry.kind === 'span_clock') {
        // Tenant-attribution guard (same class as pick_tallies above):
        // record_span_clock_in stamps tenant_id = current_tenant_id(), so a
        // drain under a different active tenant would clock the pickers into
        // the WRONG tenant (or refuse and strand a MONEY kind). DEFER.
        const clockTid = decodeStoredClaims()?.active_tenant_id ?? null
        if (entry.tenant_id && clockTid && entry.tenant_id !== clockTid) continue
        {
          const { data, error } = await withTimeout(
            supabase.rpc('record_span_clock_in', { p_scans: entry.scans }),
            DRAIN_SEND_TIMEOUT_MS,
            'span clock drain',
          )
          if (error) throw dbError(error)
          // P46-04, drain half: the live path reports a zero-insert to the
          // user, but THIS is the path most field OUTs actually take (capture
          // is offline-first), and there is no user here to tell. A full-batch
          // zero means those clock events never landed and the entry is about
          // to be deleted — the scans older than 7 days are skipped with only
          // a server-side RAISE WARNING, so without this the loss is invisible
          // everywhere. 'info' + force: queryable, never pages, survives the
          // noise swallow. A zero is often benign (everyone already
          // transitioned), which is exactly why it is info and not warning.
          if (spanClockOutcome(data) === 'noop') {
            void reportError(new Error('span_clock drain inserted 0 scans'), {
              source: 'manual', level: 'info', force: true,
              context: {
                event:       'span_clock_drain_noop',
                scan_count:  Array.isArray(entry.scans) ? entry.scans.length : null,
                queued_at:   entry.queued_at ?? null,
                tenant_id:   entry.tenant_id ?? null,
              },
            })
          }
        }
      } else if (entry.kind === 'harvest_load') {
        // Tenant-attribution guard (same class as span_clock above):
        // record_harvest_load derives everything from current_tenant_id(), so
        // a drain under a different active tenant would book the load — and
        // its delivery note — against the WRONG contractor. DEFER.
        const loadTid = decodeStoredClaims()?.active_tenant_id ?? null
        if (entry.tenant_id && loadTid && entry.tenant_id !== loadTid) continue
        {
          const load = { ...entry.load }
          // Upload the DN photo first so the note carries its evidence. A
          // failed upload must NOT lose the load — the bins and the DN number
          // are what Johan reconciles on; the photo is corroboration.
          if (entry._photoBlob && !load.dn_photo_url) {
            // The load still lands whatever happens here: bins + DN number are
            // what Johan reconciles on, the photo is corroboration.
            const dn = await uploadHarvestEvidence({
              blob: entry._photoBlob as Blob, compress: true,
              path: await tenantPathResolved(
                `${String(load.load_date ?? 'undated')}/dn-${String(load.load_id)}.jpg`,
              ),
              what: 'harvest DN photo upload failed; load kept',
              context: { load_id: String(load.load_id) },
            })
            if (dn) load.dn_photo_url = dn
          }
          const { error } = await withTimeout(
            supabase.rpc('record_harvest_load', { p_load: load }),
            DRAIN_SEND_TIMEOUT_MS,
            'harvest load drain',
          )
          if (error) throw dbError(error)
        }
      } else if (entry.kind === 'harvest_general') {
        // Tenant-attribution guard, same class as span_clock and harvest_load
        // above: record_general_work_session derives the tenant from
        // current_tenant_id(), so a drain under a different active tenant
        // would book the morning — and its clock scans — against the WRONG
        // contractor. DEFER; it drains cleanly next time they're active there.
        const genTid = decodeStoredClaims()?.active_tenant_id ?? null
        if (entry.tenant_id && genTid && entry.tenant_id !== genTid) continue
        {
          const { error } = await withTimeout(
            supabase.rpc('record_general_work_session', { p_session: entry.payload }),
            DRAIN_SEND_TIMEOUT_MS,
            'harvest general work drain',
          )
          if (error) throw dbError(error)
        }
      } else if (entry.kind === 'harvest_team_move') {
        // Same tenant-attribution guard as its harvest siblings:
        // set_team_day_move derives the tenant from current_tenant_id(), so a
        // drain under a different active tenant would lend a picker to a team
        // on the WRONG contractor's books — where the RPC would then refuse
        // the worker, stranding a write that was never wrong. DEFER; it drains
        // cleanly next time they're active there.
        const moveTid = decodeStoredClaims()?.active_tenant_id ?? null
        if (entry.tenant_id && moveTid && entry.tenant_id !== moveTid) continue
        {
          const { error } = await withTimeout(
            supabase.rpc('set_team_day_move', { p_move: entry.payload }),
            DRAIN_SEND_TIMEOUT_MS,
            'harvest team move drain',
          )
          if (error) throw dbError(error)
        }
      } else if (entry.kind === 'farm_day_lend') {
        // Same tenant-attribution guard as harvest_team_move above:
        // set_farm_day_lend derives the tenant from current_tenant_id(), so a
        // drain under a different active tenant would lend a picker to a crew
        // on the WRONG farm's books, where the RPC refuses her and the write is
        // stranded though it was never wrong. DEFER; it drains cleanly the next
        // time they are active in the origin tenant. Idempotent on lend_id.
        const lendTid = decodeStoredClaims()?.active_tenant_id ?? null
        if (entry.tenant_id && lendTid && entry.tenant_id !== lendTid) continue
        {
          const { error } = await withTimeout(
            supabase.rpc('set_farm_day_lend', { p_lend: entry.payload }),
            DRAIN_SEND_TIMEOUT_MS,
            'farm day lend drain',
          )
          if (error) throw dbError(error)
        }
      } else if (entry.kind === 'piece_bag_issue') {
        // Same tenant-attribution guard as farm_day_lend above:
        // save_piece_bag_issue derives the tenant from current_tenant_id() and
        // refuses a packer who is not in it, so a drain under another active
        // tenant would strand an issue that was never wrong. DEFER. Idempotent
        // on the client-minted issue_id.
        const bagTid = decodeStoredClaims()?.active_tenant_id ?? null
        if (entry.tenant_id && bagTid && entry.tenant_id !== bagTid) continue
        {
          const { error } = await withTimeout(
            supabase.rpc('save_piece_bag_issue', { p_issue: entry.payload }),
            DRAIN_SEND_TIMEOUT_MS,
            'piece bag issue drain',
          )
          if (error) throw dbError(error)
        }
      } else if (entry.kind === 'ppe_issue') {
        // Same tenant-attribution guard as piece_bag_issue above: post_ppe_issue
        // issues for current_tenant_id() and refuses a worker who is not on that
        // roster, so a drain under another active tenant would strand an issue
        // that was never wrong. DEFER. Idempotent on the client-minted
        // p_issue_id: the RPC answers a replay with the existing issue, so a
        // drain that died after the server committed sends it again for free.
        if (defersToOtherTenant(entry)) continue
        {
          const { error } = await withTimeout(
            supabase.rpc('post_ppe_issue', entry.payload as never),
            DRAIN_SEND_TIMEOUT_MS,
            'ppe issue drain',
          )
          if (error) throw dbError(error)
        }
      } else if (entry.kind === 'spray') {
        // Same tenant-attribution guard as spray_complete below, and for the
        // same reason: save_spray_record (mig 685) omits tenant_id from its
        // INSERT list, so the row takes DEFAULT current_tenant_id() at DRAIN
        // time. Draining a farm-A spray while farm B is active files the
        // GlobalG.A.P. record — and its chemical USE decrement — against farm
        // B. Defer instead of failing: it drains cleanly the next time the
        // user is active in the origin tenant. Legacy entries with no stamp
        // fall through to best-effort drain.
        const sprayTid = decodeStoredClaims()?.active_tenant_id ?? null
        if (entry.tenant_id && sprayTid && entry.tenant_id !== sprayTid) continue
        const sprayRes = await withTimeout(runSprayInsert({
          payload:    entry.payload,
          photo:      entry.photo,
          photo_name: entry.photo_name,
        }), DRAIN_SEND_TIMEOUT_MS, 'spray insert drain')
        if (sprayRes.plantingDegraded) {
          // eslint-disable-next-line no-console
          console.warn('[offline-queue] spray replay: planting no longer matches its block — recorded whole-block', entry.payload?.spray_id ?? entry.id)
          // Spec Phase B contract: a degrade is NEVER silent — the offline
          // operator is exactly who races a stale planting. Fire-and-forget
          // so a dismissed/blocked dialog can't stall the drain.
          void alertDialog({ message: `A queued spray synced: ${PLANTING_DEGRADED_MESSAGE}` })
        }
      } else if (entry.kind === 'spray_complete') {
        // Tenant-attribution guard (same class as pick_tallies above). The entry
        // carries its ORIGIN tenant (stamped at enqueue). complete_spray_task is
        // SECURITY INVOKER and its INSERT INTO spray_records relies on the
        // tenant_id column DEFAULT current_tenant_id() — so draining under a
        // DIFFERENT active tenant (a multi-tenant user who switched farms between
        // capturing the spray offline and this drain) would land the GlobalG.A.P.
        // spray compliance record + its chemical USE decrement in the WRONG
        // tenant, while the origin tenant's task never flips COMPLETED. That is
        // silent cross-tenant food-traceability corruption + stock non-conservation.
        // DEFER instead: leave it queued, don't count it failed, don't page. It
        // drains cleanly when the user is next active in the spray's origin tenant.
        // Only gates when BOTH the stamp and the active tenant are known and
        // differ (legacy unstamped entries fall through to best-effort drain).
        const activeTid = decodeStoredClaims()?.active_tenant_id ?? null
        if (entry.tenant_id && activeTid && entry.tenant_id !== activeTid) continue
        // mig 670 — idempotent: re-inserts the track (upsert), re-uploads the
        // signature (upsert), and complete_spray_task skips on replay.
        const completeRes = await withTimeout(runSprayComplete(entry), DRAIN_SEND_TIMEOUT_MS, 'spray complete drain')
        if (completeRes.plantingDegraded) {
          // eslint-disable-next-line no-console
          console.warn('[offline-queue] spray-complete replay: planting no longer matches its block — recorded whole-block', entry.spray_id)
          void alertDialog({ message: `A queued spray task synced: ${PLANTING_DEGRADED_MESSAGE}` })
        }
        // Out of signal at sign-off, the sprayer is long gone from the sheet:
        // the drain is the only place left to say the task was already done.
        if (completeRes.alreadyRecorded) {
          void alertDialog({ title: ALREADY_RECORDED_TITLE, message: `A queued spray sign-off synced. ${alreadyRecordedMessage(completeRes.alreadyRecorded)}` })
        }
        // Part-done sign-off: the record landed but the rest could not be planned.
        if (completeRes.followupFailed) {
          void alertDialog({ message: `A queued spray synced. ${FOLLOWUP_FAILED_NOTE}` })
        }
      } else if (entry.kind === 'reject') {
        // A queued NEW bin takes tenant_id from the column default at drain
        // (F06 D2): wait while another business is active. One with no origin
        // stamp cannot be filed safely in any business, so it stops with a
        // sentence rather than guess. An entry onto an existing open bin
        // drains as before (it names a bin; the server's guard decides).
        if (entry.new_bin) {
          if (!entry.tenant_id) {
            await markFailed(entry, new Error(NEW_BIN_UNSTAMPED_MESSAGE))
            failed += 1
            continue
          }
          if (defersToOtherTenant(entry)) continue
        }
        await withTimeout(runRejectInsert({
          payload:    entry.payload,
          new_bin:    entry.new_bin ?? null,
          photo:      entry.photo,
          photo_name: entry.photo_name,
        }), DRAIN_SEND_TIMEOUT_MS, 'reject insert drain')
      } else if (entry.kind === 'phenology') {
        // Tenant-attribution guard. upsert_phenology_event is SECURITY DEFINER
        // and stamps tenant_id := current_tenant_id(), so unlike the scouting
        // and walk arms above this one does not FAIL under the wrong tenant —
        // it succeeds there, silently, against the origin tenant's block_id.
        // Defer; it drains cleanly next time the user is in that tenant.
        if (defersToOtherTenant(entry)) continue
        await withTimeout(runPhenologyUpsert({
          payload:    entry.payload,
          photo:      entry.photo,
          photo_name: entry.photo_name,
          tenant_id:  entry.tenant_id ?? null,
          photoTries: entry.attempt_count,
        }), DRAIN_SEND_TIMEOUT_MS, 'phenology upsert drain')
      } else if (entry.kind === 'fert') {
        await withTimeout(runFertInsert({
          rows:       entry.rows,
          photo:      entry.photo,
          photo_name: entry.photo_name,
        }), DRAIN_SEND_TIMEOUT_MS, 'fert insert drain')
      } else if (entry.kind === 'fert_complete') {
        try {
          const res = await withTimeout(runFertComplete(entry), DRAIN_SEND_TIMEOUT_MS, 'fert record as done drain')
          if (res.alreadyRecorded) void alertDialog({ title: 'Already recorded', message: FERT_ALREADY_RECORDED_SYNC })
        } catch (e) {
          // The guard: never retried — a changed row stays changed. Kept,
          // with the reason, for the operator to see and clear.
          if (e instanceof FertCompleteRefused) {
            await markFailed(entry, e)
            failed += 1
            continue
          }
          throw e
        }
      } else if (entry.kind === 'irrigation') {
        await withTimeout(runIrrigationInsert({ payload: entry.payload }), DRAIN_SEND_TIMEOUT_MS, 'irrigation insert drain')
      } else if (entry.kind === 'farm_load') {
        await withTimeout(runFarmLoadInsert({ payload: entry.payload }), DRAIN_SEND_TIMEOUT_MS, 'farm load insert drain')
      } else if (entry.kind === 'security_patrol_scan') {
        await withTimeout(runSecurityPatrolScanInsert({ payload: entry.payload }), DRAIN_SEND_TIMEOUT_MS, 'patrol scan insert drain')
      } else if (entry.kind === 'drain_reading') {
        await withTimeout(runDrainReadingInsert({ payload: entry.payload }), DRAIN_SEND_TIMEOUT_MS, 'drain reading insert drain')
      } else if (entry.kind === 'qc') {
        // Bound the bare photo uploads (no per-call timeout of their own now)
        // so a stuck upload can't stall the sequential drain; a timeout is
        // network-ish → transient → retried on the next drain.
        await withTimeout(
          runQcInsert({ runPayload: entry.runPayload, responses: entry.responses, complete: entry.complete }),
          LIVE_ATTEMPT_TIMEOUT_MS * 4, 'qc drain',
        )
      } else if (entry.kind === 'delivery') {
        // A photo lost for a non-network reason is written into the
        // delivery's notes by runDeliveryInsert — nobody is at the gate to
        // tell when the queue drains.
        await withTimeout(runDeliveryInsert({
          deliveryPayload: entry.deliveryPayload,
          additionPayload: entry.additionPayload,
          newStatus:       entry.newStatus,
          photo:           entry.photo,
          photo_name:      entry.photo_name,
          load_photo:      entry.load_photo ?? null,
          load_photo_name: entry.load_photo_name ?? null,
        }), DRAIN_SEND_TIMEOUT_MS, 'delivery insert drain')
      } else if (entry.kind === 'vehicle_trip' || entry.kind === 'field_operation') {
        // Tenant-attribution guard: the row's tenant_id was stamped at enqueue
        // and the RLS check compares it to the active tenant. Defer, don't fail.
        if (defersToOtherTenant(entry)) continue
        await withTimeout(
          runFarmCaptureInsert(entry.kind === 'vehicle_trip' ? 'vehicle_trips' : 'tractor_operations', entry.payload),
          DRAIN_SEND_TIMEOUT_MS, `${entry.kind} insert drain`)
      } else if (entry.kind === 'fuel') {
        await withTimeout(runFuelInsert({ payload: entry.payload, table: entry.table }), DRAIN_SEND_TIMEOUT_MS, 'fuel insert drain')
      } else if (entry.kind === 'equipment_service') {
        await withTimeout(runEquipmentServiceInsert({
          payload:    entry.payload,
          photo:      entry.photo,
          photo_name: entry.photo_name,
          photoTries: entry.attempt_count,
        }), DRAIN_SEND_TIMEOUT_MS, 'equipment service insert drain')
      } else if (entry.kind === 'labour_attest') {
        // Audit must-fix #6 — replay the manager_attest_clock RPC.
        // Server cooldown (mig 633 #4) catches double-submits within
        // ±30s of the same scanned_at; past 30s we accept the duplicate
        // risk (documented in the modal's queued-toast).
        //
        // Tenant-attribution guard first: the RPC resolves the worker by
        // (worker_number, current_tenant_id()) at drain time, and worker
        // numbers are per-tenant sequences — draining under the wrong tenant
        // silently clocks a DIFFERENT person. Defer, don't fail.
        const attestTid = decodeStoredClaims()?.active_tenant_id ?? null
        if (entry.tenant_id && attestTid && entry.tenant_id !== attestTid) continue
        const { error } = await withTimeout(supabase.rpc('manager_attest_clock', {
          p_worker_number: entry.worker_number,
          p_status_code:   entry.status_code,
          p_reason:        entry.reason,
          p_device_id:     entry.device_id,
          p_scanned_at:    entry.scanned_at,
        }), DRAIN_SEND_TIMEOUT_MS, 'labour attest drain')
        if (error) throw dbError(error)
      } else if (entry.kind === 'labour_adjust') {
        // Audit must-fix #6 — replay adjust_labour_log_times. Idempotent
        // server-side: UPDATEs an existing log_id, so replay-safe.
        const { error } = await withTimeout(supabase.rpc('adjust_labour_log_times', {
          p_log_id:       entry.log_id,
          p_check_in_at:  entry.check_in_at,
          p_check_out_at: entry.check_out_at,
          p_reason:       entry.reason,
        }), DRAIN_SEND_TIMEOUT_MS, 'labour adjust drain')
        // M120: dbError (not the raw PostgREST object) — a raw throw stringifies
        // to "[object Object]", defeating the network/JWT/RLS classifiers and
        // stranding a transient failure on a payroll write as permanent.
        if (error) throw dbError(error)
      } else if (entry.kind === 'stock_movement') {
        // Wave 2e — replay through the same gated RPCs (FEFO / lot mint /
        // location guard re-apply). Duplicate risk on a replayed RECEIVE/ISSUE
        // is bounded by the queued-toast wording, same stance as labour_attest.
        await withTimeout(runStockMovement(entry), DRAIN_SEND_TIMEOUT_MS, 'stock movement drain')
      } else if (entry.kind === 'stock_count_line') {
        // STK-32 — replay the worksheet UPDATE. Tenant guard first: the write
        // is RLS-scoped, so draining under a different active tenant matches
        // zero rows and would strand a figure that is fine in its own tenant.
        const countTid = decodeStoredClaims()?.active_tenant_id ?? null
        if (entry.tenant_id && countTid && entry.tenant_id !== countTid) continue
        // Absolute column values keyed on line_id — replaying twice leaves the
        // row where one replay leaves it (last-write-wins on the line).
        await withTimeout(runStockCountLine(entry), DRAIN_SEND_TIMEOUT_MS, 'stock count line drain')
      }
      await deleteEntry(entry.id)
      sent += 1
    } catch (e: unknown) {
      const err  = e instanceof Error ? e : new Error(String(e))
      // W8 — dbError (thrown by the helper inserts) preserves the SQLSTATE, so
      // a helper-routed 23514 / PT429 now reaches the classifiers with its code
      // instead of undefined (previously it fell back to substring-only match).
      const code = (e as { code?: string | null } | null)?.code ?? null
      if (isTransientFault(err.message, code)) {
        await recordFailure(entry, err)
        armDrainBackoff(err.message, (err as { details?: string | null }).details)   // W6
        break
      }
      // A persistent RLS/permission denial (e.g. an offline write captured by a
      // role that can't sync it) won't heal on retry — mark it failed with a
      // clear "ask an admin" message instead of leaving it to loop and be masked
      // by a later "Load failed". One retry first absorbs a tenant-switch race.
      if (isRlsDenial(err.message)) {
        if (await disposeRlsDenial(entry)) failed += 1
        continue
      }
      // Council T3.8 — non-network failure: KEEP the row. A transient RLS
      // denial during a tenant-switch race, a 5xx, or an expired session
      // used to silently DELETE the queued write here, destroying operator
      // data with no record. Now we only mark-failed (and keep) on a
      // permanent payload fault or after the retry cap; everything else
      // records the failure and waits for the next drain.
      // The cap counts HARD attempts only (hardAttempts): a queue that spent a
      // week on marginal signal must still get its full 5 tries against a server
      // that is finally answering.
      if (isPermanent4xx(err.message, code) || (entry.kind === 'ppe_issue' && isPpeRefusal(err.message, code))
          || hardAttempts(entry) + 1 >= MAX_DRAIN_ATTEMPTS) {
        // The badge shows markFailed's message to the operator; rewrite a
        // renumbered-worker strand into a recovery instruction. reportStrandedWrite
        // keeps the RAW error — engineers want the real SQLSTATE string in Sentry.
        const opMsg = operatorStrandMessage(entry.kind, err.message)
        await markFailed(entry, opMsg ? new Error(opMsg) : err)
        await reportStrandedWrite(entry, err)
        failed += 1
      } else {
        await recordFailure(entry, err, { hard: true })
      }
    }
  }

  // W6 — any successful send means the server is answering again: reset the
  // backoff so the next queued write isn't needlessly delayed.
  if (sent > 0) clearDrainBackoff()
  if (sent + failed > 0) notifyChanged()
  return { sent, failed }
}

// ---------------------------------------------------------------------------
// Background flusher — install once, runs every minute while online and on
// the `online` event. Call from main.tsx.
// ---------------------------------------------------------------------------
export function installQueueFlusher(): void {
  if (typeof window === 'undefined') return
  const FLUSH_INTERVAL_MS = 60_000
  let timer: number | null = null

  const tryDrain = () => { void drainQueue() }

  const start = () => {
    if (timer != null) return
    tryDrain()
    timer = window.setInterval(tryDrain, FLUSH_INTERVAL_MS)
  }
  const stop = () => {
    if (timer != null) { window.clearInterval(timer); timer = null }
  }

  window.addEventListener('online',  () => { start(); tryDrain() })
  window.addEventListener('offline', stop)

  if (navigator.onLine) start()
}
